/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Address,
  NullPromise,
  QCQuoteValues,
  QuoteFrameBuyInItem,
  V6QuoteAnnotation
} from '../../../webmodule-common/interop/webmodule-interop.js';
import {
  AttachedBlobAsset,
  BinaryDataWrapper,
  CurrencyCode,
  DealerQuotePriceAdjustmentMethod,
  ExternalProviderDefinition,
  InitialPriceDefinition,
  InputCreateQuoteItem,
  InputQuoteState,
  InputUpdateQuoteItem,
  InputUpdateQuoteItems,
  InputUpdateQuotePrice,
  InputUpdateQuoteSetState,
  InputUpdateQuoteStateChangeReason,
  Quote,
  QuoteFinalStateReason,
  QuoteItem,
  QuoteItemBatch,
  QuoteItemBatchInput,
  QuoteItemBuyInData,
  QuoteItemPrice,
  QuoteItemPriceBreakdown,
  QuoteItemProviderData,
  QuoteItemRowAction,
  QuoteItemState,
  QuotePresentation,
  QuotePrice,
  QuotePriceCalculation,
  QuoteSetSibling,
  QuoteState,
  ResultFullQuotePrice,
  ResultGetQuote,
  ResultGetQuoteItemsSummary,
  ResultQuoteItem,
  UpdateQuoteItem
} from '../../api/dealer-api-interface-quote.js';
import { BlobApi } from '../../api/blob-api.js';
import {
  canAppearOnSupplierOrder,
  getQuoteNumberSuffix,
  isAnyFreehand,
  isDealerQuotePriceAdjustment,
  isFrame,
  isShipping,
  isSSI,
  mustAppearOnPurchaseOrder
} from './quote-helper-functions.js';
import { clone, compare, compareAnalyze } from '../../../webmodule-common/other/clone.js';
import { emptyGuid } from '../../../webmodule-common/other/api/guid.js';
import {
  createQuoteProviderData,
  QuoteProviderData,
  validateAndUpgradeQuoteProviderData
} from './quote-provider-data.js';
import { DevelopmentError, showDevelopmentError } from '../../../webmodule-common/other/development-error.js';
import { EventNotify } from '../../../webmodule-common/other/ui/events.js';
import {
  allItemIds,
  assignItemToGroup,
  batchMembershipChanged,
  batchMembershipMap,
  buildGroupingFromDisplayOrder,
  computeBatchItemNumbers,
  createEmptyGroupingDocument,
  createGroup,
  deleteGroup,
  excludedFromPricingCommonIds,
  findPrimaryGroup,
  flattenGroupingToDisplayOrder,
  getGroups,
  ItemPlacementRules,
  moveGroup,
  moveItem,
  normalizeBatchDate,
  normalizeGroupingDocument,
  parseGroupingDocument,
  primaryBatchAttributeKey,
  QuoteItemGroupAttributes,
  quoteItemGroupingAssetKey,
  QuoteItemBatchRow,
  QuoteItemGroupingDocument,
  reconcileBatchesIntoGrouping,
  reconcileGrouping,
  renameGroup,
  serializeGroupingDocument,
  setGroupAttributes
} from './quote-item-grouping.js';
import { fetchQuoteAsset, isSupplierUsingSSI } from './v6/helper-functions.js';
import { flagEquals, flagInSet, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions.js';
import { fromJsonStr, toJsonStr } from '../../../webmodule-common/other/blob-converters.js';
import { getQuoteSupplierDisplayName, quoteSupplierProvider } from './quoteSupplierProvider.js';
import { LangQuoteContainer } from '../../../localization/scripts/quotes/quote-container-lang';
import { localDateTimeToServer } from '../../../webmodule-common/other/datetime-converter.js';
import { LockResourceResult } from '../../../webmodule-common/other/resource-lock-info.js';
import { money } from '../../../webmodule-common/other/currency-formatter.js';
import { newGuid } from '../../../webmodule-common/other/api/guid.js';
import { needsPurchaseOrderPrompt, PurchaseOrderSettings } from './purchase-order-intent.js';
import { PricingModel, pricingModelType } from '../../../webmodule-common/pricing/pricing-model.js';
import { QuoteApi } from '../../api/quote-api.js';
import { QuoteItemContainer } from './quote-item-container.js';
import { QuoteUserInteraction } from './quote-user-interaction.js';
import { runEventNotify } from '../../../webmodule-common/other/array-helper.js';
import { sharedExecute } from '../../../webmodule-common/other/common/helpers/callbacks.js';
import { SSIProcessor } from './ssi-processor.js';
import { StockLookupView } from '../../api/dealer-api-interface-franchisee.js';
import {
  v6ConfigSupplierOptions,
  v6SupportsVersion,
  v6VersionMap
} from '../../../webmodule-common/interop/interfaces/v6-config.js';

export class MockSSIProcessor implements SSIProcessor {
  async processSSI(): Promise<InputUpdateQuoteItems | undefined> {
    return undefined;
  }
}

export interface ValidationDetails {
  code: string;
  message: string;
}

export interface PriceValidation {
  id: string;
  details: ValidationDetails[];
}

export interface StockLookupViewExtra extends StockLookupView {
  lookup: string;
  marginPercentage: number | null;
  calculatedGross: number | null;
  // description: string;
}

export interface ParamCreateQuoteItem {
  id: string | null;
  title: string | null;
  description: string | null;
  quantity: number | null;
  comment: string | null;
  commentToManufacturer: string | null;
  externalProvider: ExternalProviderDefinition | null;
  buyInData: BinaryDataWrapper | null;
  quoteItemContentType: number;
  price: InitialPriceDefinition | null;
  isRestrictedToPowerUser: boolean | null;
  isTaxableItem: boolean | null;
}

let _num = 1;
export class QuoteContainer {
  _internalId: number = _num++;
  quoteId: string;
  quote: Quote | null;
  quotePrice: QuotePrice | null;
  // A plain field, deliberately not an accessor pair. clone() uses structuredClone, which copies own
  // data properties and drops prototype accessors, so a getter here left every cloned backup without
  // the property at all - quoteChanged() then compared undefined against the real object and always
  // reported changed until resetQuote happened to assign a real own-property over the top.
  quoteProviderData: QuoteProviderData | null;
  quotePresentation: QuotePresentation | null = null;
  siblings: QuoteSetSibling[] | null = null;
  items: QuoteItem[] | null;
  deletedItems: QuoteItem[] | null;
  itemsData: QuoteItemProviderData[] | null;
  itemsBuyInData: QuoteItemBuyInData[] | null;
  itemPrices: QuoteItemPrice[] | null;
  deletedItemsData?: QuoteItemProviderData[] | null;
  deletedItemsBuyInData?: QuoteItemBuyInData[] | null;
  deletedItemPrices?: QuoteItemPrice[] | null;
  // items excluded from pricing (state ExcludeFromPricing): kept on the quote and displayed in
  // their group, but not priced and not surfaced to documents/POs/datapump. Held separate from
  // the active and deleted partitions.
  excludedItems?: QuoteItem[] | null;
  excludedItemsData?: QuoteItemProviderData[] | null;
  excludedItemsBuyInData?: QuoteItemBuyInData[] | null;
  excludedItemPrices?: QuoteItemPrice[] | null;
  isNewQuote: boolean;

  constructor(
    quoteId: string,
    quote: Quote | null,
    quotePrice: QuotePrice | null,
    quotePresentation: QuotePresentation | null,
    siblings: QuoteSetSibling[] | null,
    items: QuoteItem[] | null,
    itemsData: QuoteItemProviderData[] | null,
    itemsBuyInData: QuoteItemBuyInData[] | null,
    itemPrices: QuoteItemPrice[] | null,
    quoteProviderData: QuoteProviderData | null = null,
    isNewQuote = false
  ) {
    this.quote = quote;
    this.quotePrice = quotePrice;
    this.quotePresentation = quotePresentation;
    this.siblings = siblings;
    this.items = items;
    this.itemsData = itemsData;
    this.itemsBuyInData = itemsBuyInData;
    this.itemPrices = itemPrices;
    this.deletedItemsData = null;
    this.deletedItemsBuyInData = null;
    this.deletedItemPrices = null;
    this.excludedItems = null;
    this.excludedItemsData = null;
    this.excludedItemsBuyInData = null;
    this.excludedItemPrices = null;
    this.quoteId = quoteId;
    this.quoteProviderData = quoteProviderData;
    this.isNewQuote = isNewQuote;
  }

  reset() {
    this.quote = null;
    this.quotePrice = null;
    this.quotePresentation = null;
    this.siblings = null;
    this.items = null;
    this.itemsData = null;
    this.itemsBuyInData = null;
    this.itemPrices = null;
    this.deletedItemsData = null;
    this.deletedItemsBuyInData = null;
    this.deletedItemPrices = null;
    this.excludedItems = null;
    this.excludedItemsData = null;
    this.excludedItemsBuyInData = null;
    this.excludedItemPrices = null;

    this.quoteProviderData = null;
  }
}

export interface IQuoteValidator {
  valid: (state: QuoteState) => Promise<boolean>;
}

/** the three item partitions held by a QuoteContainer */
export type ItemPartition = 'active' | 'excluded' | 'deleted';

// a manager that is used to find and collect quote item information
// and keep a comparison backup
export class QuoteContainerManager {
  backup: QuoteContainer;
  container: QuoteContainer;
  api: QuoteApi;
  afterSave: EventNotify[] = [];
  blobApi: BlobApi;
  /** Progress, confirmation and notification. Injected so the data layer owns no UI - see quote-user-interaction.ts. */
  protected readonly interaction: QuoteUserInteraction;
  protected quoteUpdateAssets: { [key: string]: AttachedBlobAsset } | null = null;
  /**
   * the quote item grouping document; source of truth for the quote items view display order and
   * grouping. kept on the manager (not the container) so container clone/compare semantics are
   * untouched; dirty state is carried by quotePresentation.itemDisplayOrder and the pending asset.
   */
  protected itemGrouping: QuoteItemGroupingDocument | null = null;

  /**
   * True when {@link itemGrouping} was INVENTED because the stored blob could not be read, rather than parsed
   * from it. Set only when a blob genuinely exists and could not be fetched or parsed — a quote that has never
   * had one is not "unavailable", its fallback document is simply the truth.
   *
   * While set, the document must not be treated as a statement about batches: it has no groups, and saving
   * from it would report "no batches" and delete every batch row on the quote over a transient failure.
   */
  protected itemGroupingUnavailable = false;

  /**
   * True when the load-time heal (see {@link _needsItemGrouping}) corrected {@link itemGrouping} against
   * the DB batch state but, per that heal's rule, left the correction in memory only. A view that is about
   * to hand the quote to something which reads the SAVED blob rather than this in-memory document (a
   * server-rendered report, a purchase order) must flush the correction first via
   * {@link persistHealedItemGrouping}, or it will build from the stale blob the screen itself no longer
   * shows (bug 231259). Cleared once {@link applyItemGrouping} writes a document, healed or otherwise.
   */
  protected itemGroupingHealedUnsaved = false;

  /** The quote's DB delivery batches (from the last items-summary load). Empty when unbatched. */
  protected quoteItemBatches: QuoteItemBatch[] = [];

  /**
   * Runtime item→batch membership (QuoteItem.commonId → batch id), loaded from the DB link table on the
   * items-summary load and updated as SSI / grouping change it. This is the authoritative membership the
   * SSI reconcile keys off (the item rows no longer carry it). commonId absent = ungrouped.
   */
  protected itemBatchMembership = new Map<string, string>();

  /**
   * True when {@link quoteItemBatches}/{@link itemBatchMembership} were just (re)loaded from the server and
   * the presentation blob has NOT yet been healed against them. The load-time reconcile
   * (reconcileBatchesIntoGrouping) may run ONLY while this is set: it heals the blob against the DB, so it
   * must see a fresh DB snapshot. Between saves the snapshot goes stale (a grouping mutation writes batches
   * to the DB but does not reload the summary), and running the reconcile then would wrongly treat the
   * just-created batch as "deleted from the DB" and strip the group. Set on every real items-summary fetch,
   * cleared the moment the reconcile consumes it.
   */
  private batchSnapshotFresh = false;
  numberSeed: string | null = null;
  lockInfo?: LockResourceResult;
  priceValidation: PriceValidation[] | null = null;
  quoteValidator?: IQuoteValidator;
  public forceLockout = false;
  public pricingModel: PricingModel = new PricingModel(pricingModelType.margin, 0, 99);
  protected stateChangeReason: InputUpdateQuoteStateChangeReason | null = null;
  /**
   * create and return a container for quote item data references
   * @param quoteItemId the quote item to return
   * @param useBackup if true, return the backup copy, if false the working copy
   * @returns a quoteItemContainer with the data parts combined
   */
  private itemContainerCache: QuoteItemContainer[] = [];
  private itemContainerCacheBak: QuoteItemContainer[] = [];

  constructor(original: QuoteContainer, quoteApi: QuoteApi, blobApi: BlobApi, interaction: QuoteUserInteraction) {
    this.api = quoteApi;
    this.blobApi = blobApi;
    this.interaction = interaction;
    if (original.quote && original.quote.id !== original.quoteId)
      throw new Error(LangQuoteContainer.invalidArgumentQuoteId()); // Localized
    this.container = original;
    this.backup = this.clone(this.container);
  }

  get isV6(): boolean {
    return this.container.quote?.serviceProvider === quoteSupplierProvider.v6;
  }

  public get quoteState(): QuoteState {
    if (globalThis?.dealerQuoteState !== undefined) return globalThis?.dealerQuoteState;
    return this.quote.state;
  }

  public get quoteCurrency(): CurrencyCode {
    return this.quote.quoteCurrency;
  }

  public set quoteState(value: QuoteState) {
    this.quote.state = value;
  }

  get siblings() {
    return this.container.siblings;
  }
  get hasDeletedItems(): boolean {
    return this.container.deletedItems?.length ? true : false;
  }

  hasBreakdowns(id: string) {
    const price = this.container.itemPrices?.find(x => x.id === id);
    return price?.breakdowns && price?.breakdowns.length > 0;
  }

  get isSSINeedsShippingAddress(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.ssi.trigger.shipping';
      if (!(featureFlag in customOptions)) {
        return false;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return false;
    }
  }

  //default behavour true unless disabled
  get is3dModelEnabled(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.3d.model.enabled';
      if (!(featureFlag in customOptions)) {
        return true;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return true;
    }
  }

  get isPricingMetricLockedBySupplier(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.supplier-margin-markup-override.enabled';
      if (!(featureFlag in customOptions)) {
        return false;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Does this supplier's V6 understand quote item batches (MRD, Task 230006)?
   *
   * Item groups always exist in Dealer as a presentation/organisation device. This flag gates only the
   * parts of a group that mean something to V6: the batch metadata (delivery date, customer ref,
   * comment, status), emitting `quote.batches[]` / `quoteItem.quoteItemBatchId` into the V6 contract,
   * and per-batch SSI. With the flag off a group is purely a Dealer-side folder - nothing about it
   * reaches V6, so nothing about it can go stale either.
   *
   * Default when absent is FALSE: a supplier that has not opted in is assumed not to support batches.
   * Stored batch metadata is preserved either way (it is simply not shown or sent), so turning the flag
   * on later brings existing data back intact.
   */
  get isV6BatchLinkingEnabled(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.batches.v6-linked.enabled';
      if (!(featureFlag in customOptions)) {
        return false;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return false;
    }
  }

  public get hasFrames(): boolean {
    const items = this.container.items;
    if (!items) return false;

    for (const item of items) {
      if (isFrame(item)) return true;
    }
    return false;
  }

  /**
   * the quoteId for this managed container
   */
  get quoteId(): string {
    return this.container.quoteId;
  }

  get quoteItemPriceTotal(): number {
    return this.getQuoteItemPriceTotal();
  }

  get taxableQuoteItemPriceTotal(): number {
    return this.getTaxableQuoteItemPriceTotal();
  }

  public get quoteTitle(): string {
    if (this.quote.quoteNumber != 0)
      return LangQuoteContainer.quoteTitleNumber({
        quoteNumber: getQuoteNumberSuffix(this.quote),
        title: this.quote.title
      });
    else return LangQuoteContainer.quoteTitleDraft({ title: this.quote.title });
  }

  /**
   * returns the quote object after needsQuote is called, or throws an error if the quote is unavailable.
   */
  get quote(): Quote {
    if (!this.container.quote) {
      throw new Error(LangQuoteContainer.quoteIsNull()); // Localized
    }
    return this.container.quote;
  }

  get quoteItems(): QuoteItem[] {
    if (!this.container.items) throw new Error(LangQuoteContainer.callNeedsQuoteItemsFirst()); // Localized
    return this.container.items;
  }

  get quoteItemPrices(): QuoteItemPrice[] {
    if (!this.container.itemPrices) throw new Error(LangQuoteContainer.callNeedsQuoteItemsFirst()); // Localized
    return this.container.itemPrices;
  }

  /**
   * returns the quote price object after needsQuote is called, or throws an error if the quote price is unavailable.
   */
  get quotePrice(): QuotePrice {
    if (!this.container.quotePrice) {
      throw new Error(LangQuoteContainer.quotePriceIsNull()); // Localized
    }
    return this.container.quotePrice;
  }

  private _lastSaveSuccessful = true;

  public get lastSaveSuccessful(): boolean {
    return this._lastSaveSuccessful;
  }

  public get isSupplierQuote(): boolean {
    if (!this.container.items) throw new DevelopmentError('call needsQuoteItems first');

    const items = this.container.items.filter(item => canAppearOnSupplierOrder(item)) ?? [];

    // if we end up with free hand items and/or shipping only, we should not create a purchase order
    const shouldCreatePO = items.some(item => mustAppearOnPurchaseOrder(item));
    return shouldCreatePO;
  }

  public get isBuyInDataLocked(): boolean {
    // eslint-disable-next-line no-bitwise
    return !flagInSet(this.quoteState, QuoteState.Draft | QuoteState.Active);
  }

  private _supplierName?: string;

  get supplierName(): string {
    return this._supplierName ?? '';
  }

  get dealerPriceAdjustment(): number {
    const item = this.quoteItems.find(x => isDealerQuotePriceAdjustment(x));
    if (!item) return this.quotePrice.priceAdjustment;
    const itemprice = this.quoteItemPrices.find(x => x.id === item.id);
    return (itemprice?.calculatedGrossSellingPrice ?? 0) + this.quotePrice.priceAdjustment;
  }

  get taxRate() {
    return this.quotePrice.taxPercentage / 100;
  }

  public get allowNonTaxableItems(): boolean {
    return this.container.quote?.allowNonTaxableItems ?? false;
  }

  public static async createQuoteServiceProviderData(
    supplierType: string,
    supplierId: string,
    quoteOwnerId: string
  ): NullPromise<QuoteProviderData> {
    return await createQuoteProviderData(supplierType, supplierId, quoteOwnerId);
  }

  applySupplierMarginMarkupOverride(value: number, valueType: 'margin' | 'markup' = 'margin') {
    this.quotePrice.percentMarginOrMarkup = value;
    this.quotePrice.calculation = valueType === 'margin' ? QuotePriceCalculation.Margin : QuotePriceCalculation.Markup;
  }

  getQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    //if we dont sum this to 2dp, then the UI wont always add up if someone checking.
    //the server will be doing the real calculations anyway
    (itemPrices ?? this.container.itemPrices)?.forEach(x => (total += money(x.calculatedGrossSellingPrice, 2)));
    return total;
  }
  getQuoteItemDealerPriceAdjustmentTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => (total += money(x.priceAdjustment, 2)));
    return total;
  }
  getTaxableQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    (itemPrices ?? this.container.itemPrices)?.forEach(x => {
      if (x.isTaxableItem && this.quotePrice.taxPercentage !== 0) total += money(x.calculatedGrossSellingPrice, 2);
    });
    return total;
  }

  getNonTaxableQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    (itemPrices ?? this.container.itemPrices)?.forEach(x => {
      if (!x.isTaxableItem || this.quotePrice.taxPercentage === 0) total += money(x.calculatedGrossSellingPrice, 2);
    });
    return total;
  }

  public async updatePricingRules() {
    throw new DevelopmentError('updatePricingRules not implemented');
  }

  async getInstallationAddress(): Promise<Address | null> {
    return null;
  }

  /**
   * @param _pendingBatchAssignments batch membership the quote is about to have, keyed by quote item id
   * (null = ungrouped). Supply it whenever the save that follows will change membership, so SSI is
   * generated against the post-save shape rather than the current document.
   */
  public ssiProcessor(
    _itemFilter?: string[] | undefined,
    _extraItems?: QuoteItemContainer[],
    _pendingBatchAssignments?: ReadonlyMap<string, string | null>
  ): SSIProcessor {
    return new MockSSIProcessor();
  }

  public async isQuoteValid(state: QuoteState): Promise<boolean> {
    if (!this.quoteValidator) return true;

    return await this.quoteValidator.valid(state);
  }

  public addPriceValidation(id: string, code: string, message: string) {
    this.priceValidation ??= [];

    let validation = this.priceValidation.find(x => x.id == id);

    if (!validation) {
      validation = new (class implements PriceValidation {
        details: ValidationDetails[] = [];
        id = id;
      })();

      this.priceValidation.push(validation);
    }

    if (validation.details.findIndex(x => x.code == code) < 0) validation.details.push({ code, message });
  }

  public getPriceValidation(id: string) {
    return this.priceValidation?.find(x => x.id == id);
  }

  isReadonly(): boolean {
    return this.internalIsReadonly() || this.isLockedFromUse();
  }

  isLockedFromUse(): boolean {
    return this.forceLockout || !(this.lockInfo?.isLockOwner ?? true);
  }

  /**
   * Readonly for reasons OTHER than somebody else holding the interactive lock: the quote's own state,
   * or a forced lockout (supplier offline, an operation in flight).
   *
   * Exists for `AppResourcePage.isResourceReadonly`, whose contract is explicitly "separate from
   * lock-based readonly". Answering that with plain `isReadonly()` folds the foreign lock back in, and
   * the page then renders a bare "Readonly" badge instead of "Currently in use by {name}" — the owner
   * is the one thing the user actually needs there. The project page shows it because it does not
   * override that method.
   */
  isReadonlyIgnoringForeignLock(): boolean {
    return this.internalIsReadonly() || this.forceLockout;
  }

  /**
   *
   * @param id the quoteItemId
   * @param returnNextPositionIfNotExists
   */
  public itemPosition(id?: string, returnNextPositionIfNotExists?: boolean): number | undefined {
    if (id === undefined) return undefined;
    //TODO - implement a quote item position tracker, managed at the quote level and use this for rendering
    const index = this.container.items?.findIndex(x => x.id === id) ?? -1;
    return index === -1 && returnNextPositionIfNotExists ? (this.container.items?.length ?? 1) : index + 1;
  }

  /**
   * Simple wrapper around structuredClone
   * @param item an object of any basic type to clone
   * @returns
   */
  clone<ItemType>(item: ItemType): ItemType {
    return clone(item);
  }

  /**
   * Locate a price object by quote item id searching active then deleted partitions.
   * @param id Quote item id
   * @param useBackup When true searches backup partitions instead of working container
   */
  private findItemPrice(id: string, useBackup = false): QuoteItemPrice | undefined {
    const c = useBackup ? this.backup : this.container;
    return c.itemPrices?.find(x => x.id === id) || c.deletedItemPrices?.find(x => x.id === id) || undefined;
  }

  getQuoteItemExtendedListPrice(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    const price = breakdown ?? this.findItemPrice(id);
    if (price == undefined) return 0;

    return price.supplierGrossQuantityCost;
  }

  getQuoteItemExtendedListPriceTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const item = this.getItemById(x.id);
      if (!item) return; //Deleted Item
      if (!isAnyFreehand(item)) total += money(x.supplierGrossQuantityCost, 2);
    });
    return total;
  }

  getQuoteItemMultiplier(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    const price = breakdown ?? this.findItemPrice(id);
    if (price == undefined) return 0;

    return (
      price.supplierNettQuantityCost /
      (price.supplierGrossQuantityCost + (price.supplierGrossQuantityCost == 0 ? 1 : 0))
    );
  }

  getQuoteItemMultiplierTotal(): number {
    let supplierNettQuantityCostTotal = 0;
    let supplierGrossQuantityCostTotal = 0;

    this.container.itemPrices?.forEach(x => {
      const item = this.getItemById(x.id);
      if (!item) return; //Deleted Item
      if (!isAnyFreehand(item)) {
        supplierNettQuantityCostTotal += money(x.supplierNettQuantityCost, 2);
        supplierGrossQuantityCostTotal += money(x.supplierGrossQuantityCost, 2);
      }
    });
    return (
      supplierNettQuantityCostTotal / (supplierGrossQuantityCostTotal + (supplierGrossQuantityCostTotal == 0 ? 1 : 0))
    );
  }

  getQuoteItemSupplierPriceAdjustment(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item (active or deleted)
    const price = this.findItemPrice(id);
    if (!price) return 0;
    const supplierAdjustment = price.supplierPriceAdjustmentEnabled ? price.supplierPriceAdjustment : 0;
    if (breakdown) {
      // Calculate proportional supplierPriceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakdown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;

      const proportionalAdjustment = supplierAdjustment * proportion;
      return proportionalAdjustment;
    }

    return supplierAdjustment;
  }

  getQuoteItemSupplierPriceAdjustmentTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const supplierAdjustment = x.supplierPriceAdjustmentEnabled ? x.supplierPriceAdjustment : 0;
      const item = this.getItemById(x.id);
      if (!item) return; //Deleted Item
      if (isAnyFreehand(item)) total += money(supplierAdjustment, 2);
    });
    return total;
  }

  getQuoteItemDealerCost(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item (active or deleted)
    const price = this.findItemPrice(id);
    if (!price) return 0;
    const supplierAdjustment = price.supplierPriceAdjustmentEnabled ? price.supplierPriceAdjustment : 0;
    if (breakDown) {
      // Calculate proportional priceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakDown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;
      const proportionalAdjustment = supplierAdjustment * proportion;
      return breakdownGross + proportionalAdjustment;
    }

    return price.supplierNettQuantityCost + supplierAdjustment;
  }

  getQuoteItemDealerCostTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const supplierAdjustment = x.supplierPriceAdjustmentEnabled ? x.supplierPriceAdjustment : 0;
      total += money(x.supplierNettQuantityCost + supplierAdjustment, 2);
    });
    return total;
  }

  getQuoteItemMarginOrMarkupPercent(id: string): number {
    const price = this.findItemPrice(id);
    if (price == undefined) return 0;

    return price.percentMarginOrMarkup ?? this.quotePrice.percentMarginOrMarkup;
  }

  getQuoteItemDealerPriceAdjustment(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item (active or deleted)
    const price = this.findItemPrice(id);
    if (!price) return 0;

    if (breakDown) {
      // Calculate proportional priceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakDown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;
      const proportionalAdjustment = price.priceAdjustment * proportion;
      return proportionalAdjustment;
    }

    return price.priceAdjustment;
  }

  quoteItemTax(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    const price = this.findItemPrice(id);
    if (price === undefined) return 0;

    const gross = breakDown?.calculatedGrossSellingPrice ?? price.calculatedGrossSellingPrice;
    return price.isTaxableItem ? gross * this.taxRate : 0;
  }

  quoteItemPrice(id: string, removeBuyInCost = false): number {
    const price = this.findItemPrice(id);
    if (price == undefined) return 0;

    let linePrice = price.calculatedGrossSellingPrice;

    if (removeBuyInCost) {
      const quoteItemBuyInData = this.container.itemsBuyInData?.find(x => x.id == id);

      if (quoteItemBuyInData != undefined) {
        const buyIns = fromJsonStr<StockLookupViewExtra[]>(quoteItemBuyInData.buyInData);

        const sum =
          buyIns?.reduce((accumulator, currentObject) => {
            return accumulator + (currentObject.calculatedGross ?? 0);
          }, 0) ?? 0;

        linePrice -= sum;
      }
    }

    return linePrice;
  }

  public needsQuote = sharedExecute(this._needsQuote.bind(this));
  /**
   * this will ensure at an async level that the quote propery is valid, before accessing the property synchronously
   * @returns true if the quote property is now valid
   */
  protected async _needsQuote(): Promise<boolean> {
    //override
    return Promise.resolve(false);
  }

  async getFreshQuoteCopy(): NullPromise<ResultGetQuote> {
    return await this.api.getQuote({ quoteId: this.quoteId });
  }

  public generateQuoteNumberOnSave() {
    this.numberSeed = this.quote.quoteOwnerId;
  }

  protected isSupplierReviewEnabled(): Promise<boolean> {
    return Promise.resolve(true);
  }

  async quoteStateChange(state: QuoteState): Promise<boolean> {
    const originalState = this.quoteState;
    const originalAllowSupplierApproval = this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval;
    // The purchase order prompt writes the dealer's order number onto the quote; a failed or cancelled
    // change must not leave it behind.
    const originalCustomQuoteNumber = this.quote.customQuoteNumber;
    const seed = this.numberSeed;
    const qn = this.quote.quoteNumber;

    const restoreQuoteState = () => {
      this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval = originalAllowSupplierApproval;
      this.quote.customQuoteNumber = originalCustomQuoteNumber;
      this.quoteState = originalState;
      this.quote.quoteNumber = qn;
      this.numberSeed = seed;
      this.stateChangeReason = null;
    };
    const allowSupplierReview = await this.isSupplierReviewEnabled();
    const addSiblingStates = async (input: InputQuoteState[], rollback?: boolean) => {
      if (
        (state === QuoteState.Accepted && !this.quote.supplierApproved) ||
        (state === QuoteState.Approved && !allowSupplierReview)
      ) {
        //if we are here, we need to locate the rejected reason, and then reject any quotes
        //that are active or issued at the same time as accepting this quote
        const inputRejection = this.getQuoteSuperceededResult();
        this.siblings
          // eslint-disable-next-line no-bitwise
          ?.filter(x => flagInSet(x.state, QuoteState.Active | QuoteState.IssuePending | QuoteState.Issued))
          .forEach(s => {
            input.push({
              quoteId: s.quoteId,
              state: rollback ? s.state : QuoteState.Rejected,
              stateChangeReason: rollback ? null : inputRejection,
              expectedCurrentState: null
            });
          });
      }
    };

    const performSave = async (rollback?: boolean) => {
      if (
        flagInSet(
          originalState,
          // eslint-disable-next-line no-bitwise
          QuoteState.Draft |
            QuoteState.Active |
            QuoteState.IssuePending |
            QuoteState.SupplierReviewPending |
            QuoteState.SupplierReviewed
        ) ||
        (originalState === QuoteState.Issued && !this.quote.supplierApproved)
      ) {
        let siblingUpdate: InputQuoteState[] | null = [];
        if (
          (this.quoteState === QuoteState.Accepted && !this.quote.supplierApproved) ||
          (this.quoteState === QuoteState.Approved && !allowSupplierReview)
        ) {
          await addSiblingStates(siblingUpdate, rollback);
          if (siblingUpdate.length === 0) siblingUpdate = null;
          // The no-review route asks in quoteStateBeforeChange(Approved), before the purchase order exists.
          if (this.isSupplierQuote && allowSupplierReview && !(await this.resolvePurchaseOrderIntent(true)))
            return false;
        }

        //we need to use saveQuote here because the quote may have other things changed and edited.
        return await this.saveQuote(rollback, siblingUpdate);
      } else {
        const input: InputUpdateQuoteSetState = {
          quoteState: [
            {
              quoteId: this.quote.id,
              state: this.quoteState,
              stateChangeReason: null,
              expectedCurrentState: null
            }
          ]
        };
        await addSiblingStates(input.quoteState, rollback);
        return await this.saveQuoteSetState(input);
      }
    };

    // quoteStateBeforeChange can prompt for the purchase order intent before refusing the change.
    if (!(await this.quoteStateBeforeChange(state, originalState))) {
      restoreQuoteState();
      return false;
    }

    this.quoteState = state;
    try {
      if (!(await performSave())) {
        restoreQuoteState();
        return false;
      }

      if (!(await this.quoteStateAfterChange(state, originalState))) {
        restoreQuoteState();
        await performSave(true);
        return false;
      }
    } catch (e) {
      restoreQuoteState();
      await performSave(true);
      throw e;
    }
    return true;
  }

  /** The supplier's V6 order settings for this quote's supplier. */
  protected purchaseOrderSettings(): PurchaseOrderSettings {
    const v6Settings = v6ConfigSupplierOptions(this.quote.supplierId);
    return {
      poNumberRequired: v6Settings.isPurchaseOrderNumberMandatory,
      forceIssueOrder: v6Settings.isPurchaseOrderIssuedImmediately
    };
  }

  /**
   * Ask the dealer what happens to the purchase order on approval: their order number, and - on a
   * review where the supplier settings leave it open - whether the supplier may issue it. Without a
   * review this only asks when a setting obliges it. Returns false if the dealer cancelled.
   */
  async resolvePurchaseOrderIntent(reviewing: boolean): Promise<boolean> {
    const settings = this.purchaseOrderSettings();
    if (!needsPurchaseOrderPrompt(reviewing, settings)) return true;

    const response = await this.interaction.confirmTechnicalReview({
      ...settings,
      reviewing,
      supplierName: this.supplierName,
      currentOrderNumber: this.quote.customQuoteNumber ?? ''
    });
    if (!response) return false;

    // The setting is an override - the dialog offers no way to decline it, and neither does this.
    this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval =
      settings.forceIssueOrder || response.allowSupplierToIssue;
    this.quote.customQuoteNumber = response.orderNumber;
    return true;
  }

  async saveQuoteSetState(input: InputUpdateQuoteSetState) {
    const result = await this.api.updateQuoteSetState(input);
    if (!result) return false;
    const thisState = result.items.find(x => x.quoteId === this.quote.id);
    if (!thisState) throw new DevelopmentError(LangQuoteContainer.updateQuoteStateMissingThisQuote()); // Localized
    this.quoteState === thisState.state;
    this.quote.supplierApproved = thisState.supplierApproved;
    this.quote.recordVersion = thisState.recordVersion;
    this.backup.quote = clone(this.quote);
    result.items.forEach(thisState => {
      const sib = this.siblings?.find(x => x.quoteId === thisState.quoteId);
      if (sib) {
        sib.state = thisState.state;
        sib.recordVersion = thisState.recordVersion;
        sib.supplierApproved = thisState.supplierApproved;
      }
    });
    this.backup.siblings = clone(this.siblings?.filter(x => x.quoteId !== this.quote.id) ?? null);
    await this.doAfterSave();
    return true;
  }

  getQuoteSuperceededResult(): InputUpdateQuoteStateChangeReason {
    const x: InputUpdateQuoteStateChangeReason = {
      comment: '',
      finalStateReason: QuoteFinalStateReason.Superseded
    };
    return x;
  }

  async internalSaveQuote(siblings?: InputQuoteState[] | null) {
    this.quote.serviceProviderData = null;
    this.stageQuoteProviderDataAsset();
    const result = await this.api.updateQuote({
      siblingQuoteState: siblings ?? null,
      quote: this.quote,
      quotePrice: this.quotePrice,
      numberSeed: this.numberSeed,
      stateChangeReason: this.stateChangeReason,
      quotePresentation: this.container.quotePresentation,
      assets: this.quoteUpdateAssets,
      providerDataDifference: null,
      // Delivery batches (MRD) derived from the current grouping doc — committed atomically with the quote.
      ...this.buildBatchSaveInputs()
    });

    // Everything staged has now gone up, so the pending set must be dropped here and not only in
    // saveQuote(). internalUpdateQuoteItems calls this directly and never clears, so a `data` asset
    // left pending would keep hasPendingQuoteAssets - and therefore quoteChanged() - true for the
    // life of the manager: an extra updateQuote round-trip on every later item edit, and a quote
    // view stuck reporting unsaved changes.
    if (result) this.quoteUpdateAssets = null;
    return result;
  }

  /**
   * Stage the current in-memory quoteProviderData (IGU, default options, SSI quote calculations,
   * annotations) as the quote's `data` asset. `providerDataDifference` on InputUpdateQuote is unused
   * by every caller in this client, so this asset is the ONLY thing that actually persists
   * quoteProviderData - any save path that skips it silently drops in-memory provider data changes.
   * That gap let a delete-triggered SSI recalculation (which zeroes quoteCalculations in memory) go
   * unsaved by the auto-save in internalUpdateQuoteItems, so a reload brought the stale, pre-delete
   * values back (Bug 230258).
   */
  protected stageQuoteProviderDataAsset(): void {
    this.updatePendingQuoteAssets({
      data: {
        assetPath: this.api.createQuoteAssetPath('data', '.json'),
        data: toJsonStr(this.container.quoteProviderData) ?? null,
        delete: false,
        dataBase64: null,
        hasContent: false
      }
    });
  }

  async runPricingAdjustments(options: {
    method: DealerQuotePriceAdjustmentMethod;
    value: number;
    isTaxableAdjustment: boolean;
    percentMarginOrMarkup?: number;
    dryRun: boolean;
    quoteItems?: QuoteItem[];
    quoteItemPrices?: QuoteItemPrice[];
    quotePrice?: QuotePrice;
    adjustmentTitle?: string;
    adjustmentDescription?: string;
    adjustmentComment?: string;
  }): NullPromise<{ quotePrice: QuotePrice; itemPrices: QuoteItemPrice[]; items: QuoteItem[] }> {
    if (options.dryRun) {
      if (!options.quoteItems || !options.quotePrice || !options.quoteItemPrices)
        throw new DevelopmentError('dryRun is true, but quote data is not provided');
    } else {
      if (options.quoteItems || options.quotePrice || options.quoteItemPrices)
        throw new DevelopmentError('dryRun is false, but quote data is provided');
    }
    const quotePrice = options.quotePrice ?? this.quotePrice;
    let quoteItems = options.quoteItems ?? this.quoteItems;
    const quoteItemPrices = options.quoteItemPrices ?? this.quoteItemPrices;
    let adjustmentPriceItem = quoteItems.find(x => isDealerQuotePriceAdjustment(x));

    if (options.percentMarginOrMarkup !== undefined) quotePrice.percentMarginOrMarkup = options.percentMarginOrMarkup;

    const input: InputUpdateQuotePrice = {
      quotePrice: quotePrice,
      quoteItemPrices: quoteItemPrices,
      commitChanges: !options.dryRun,
      quoteItems: quoteItems,
      dealerQuotePriceAdjustment: {
        adjustmentMethod: options.method,
        adjustmentValue: options.value,
        isTaxable: options.isTaxableAdjustment,
        title: options.adjustmentTitle ?? null,
        description: options.adjustmentDescription ?? null,
        comment: options.adjustmentComment ?? null
      }
    };
    const result = await this.api.updateQuotePrice(input);
    if (options.dryRun) {
      if (result?.resultFullQuotePrice) {
        adjustmentPriceItem = result.updatedQuoteItems.length > 0 ? result.updatedQuoteItems[0] : undefined;
        quoteItems = adjustmentPriceItem
          ? [...(quoteItems?.filter(x => !isDealerQuotePriceAdjustment(x)) ?? []), adjustmentPriceItem]
          : quoteItems?.filter(x => !isDealerQuotePriceAdjustment(x));

        // An item price came back for an item this dry run does not know about. Throw rather than
        // report and carry on: the result is not merely suspect, its totals are already wrong.
        // QuotePriceSummaryHelper sums EVERY price it holds into CalculatedLineItemTotal, so a price
        // with no item silently adds its gross selling price to the quote, and the caller assigns
        // whatever it gets straight into the numbers the user reads and then commits from. Throwing
        // leaves the dialog on its last consistent state instead of adopting an inconsistent one.
        // Nothing needs unwinding: a dry run is no-tracking server side and never saves. The server
        // rejects the same shape on the way in (QuotePriceSummaryHelper.SetData); this stays because
        // only the caller can see what the returned adjustment item did to the local item list.
        if (result.resultFullQuotePrice.itemPrices.some(x => !quoteItems.find(y => y.id === x.id))) {
          throw new DevelopmentError('runPricingAdjustments: item price returned for an unknown quote item');
        }
        return {
          quotePrice: result.resultFullQuotePrice.quotePrice,
          itemPrices: result.resultFullQuotePrice.itemPrices,
          items: quoteItems ?? []
        };
      }
      return null;
    } else if (result) {
      if (result.updatedQuoteItems.length === 0) {
        const adjustmentPriceItem = this.quoteItems.find(x => isDealerQuotePriceAdjustment(x));
        if (adjustmentPriceItem)
          this.internalDeleteQuoteItemReference(adjustmentPriceItem.id, QuoteItemRowAction.HardDelete);
      } else {
        const adjustmentPriceItem = result.updatedQuoteItems[0];
        const adjustmentPrice =
          result.resultFullQuotePrice?.itemPrices.find(x => x.id === adjustmentPriceItem.id) ?? null;

        await this.processResultItem({
          quoteItem: adjustmentPriceItem,
          quoteItemPrice: adjustmentPrice,
          quoteItemBuyInData: null,
          quoteItemProviderData: null
        });
        this.performItemSorting();
      }

      await this.resetQuote(
        result.quote!, //never null when the input was not null
        result.resultFullQuotePrice!.quotePrice,
        result.quotePresentation,
        result.siblings, //as above
        this.container.quoteProviderData //no change
      );

      this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
      this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);
      await this.validateBuyInPrices();

      this.interaction.notifySuccess(LangQuoteContainer.quoteSaved({ title: this.quoteTitle }));
      await this.doAfterSave();

      return {
        quotePrice: this.quotePrice,
        itemPrices: this.quoteItemPrices,
        items: this.quoteItems
      };
    } else return null;
  }

  /**
   * this is called to send the current quote and quote price objects to update the server.
   * on sucessful update, the internal quote and price objects will be replaced with the new copies
   * from the server
   * when updating prices we also refresh all pricing for all items at the same time that may have been
   * reliant on the pricing.
   * calling this may result in some quoteItemContainers holding outdated objects that need replacement
   */
  async saveQuote(silently?: boolean, siblings?: InputQuoteState[] | null): Promise<boolean> {
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return false;
    }
    const result = await this.internalSaveQuote(siblings);

    if (result) {
      this.quoteUpdateAssets = null;
      //quote update doesn't alter presentation, so we keep what we have
      await this.resetQuote(
        result.quote!, //never null when the input was not null
        result.resultFullQuotePrice!.quotePrice,
        result.quotePresentation,
        result.siblings,
        this.container.quoteProviderData //as above
      );
      this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
      this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);
      await this.validateBuyInPrices();

      if (!silently) this.interaction.notifySuccess(LangQuoteContainer.quoteSaved({ title: this.quoteTitle }));
      await this.doAfterSave();
      ``;
      return true;
    }
    return false;
  }

  /**
   * this will find the backup of a quote item and related data, and replace the items with a clone
   * of the backup. this will make any quoteItemContainers to this id stale and need replacing
   *
   * @param id the quote item id to replace
   * @returns a reference to the fresh copy of the data
   */
  restoreQuoteItemFromBackup(id: string | undefined): QuoteItemContainer | null {
    if (!id) return null;
    const backup = this.quoteItemContainer(id, true);
    this.replaceQuoteItem(backup, false);
    this.removeContainerCache(id);
    return this.quoteItemContainer(id);
  }

  changedItem(id: string | undefined): boolean {
    function comparable(qic: QuoteItemContainer) {
      const { clientSideCache, ...backup } = qic;
      return backup;
    }

    if (!id) return false;
    const backup = comparable(this.quoteItemContainer(id, true));
    const current = comparable(this.quoteItemContainer(id, false));

    const changed = !compare(backup, current);
    if (changed && globalThis?.isDevelopmentMode) {
      const backupFrame = JSON.parse(backup.data?.providerData ?? '{}');
      const currentFrame = JSON.parse(current.data?.providerData ?? '{}');
      console.debug('Quote item changed');
      console.debug('Frame Changes');
      console.debug(compareAnalyze(backupFrame, currentFrame));
      console.debug('Price Changes');
      console.debug(compareAnalyze(backup.price, current.price));
      console.debug('Item Changes');
      console.debug(compareAnalyze(backup.item, current.item));

      console.debug(backupFrame, currentFrame);
    }
    return changed;
  }

  changedItemQuantity(id: string | undefined): boolean {
    if (!id) return false;
    const backup = this.quoteItemContainer(id, true);
    const current = this.quoteItemContainer(id, false);

    return backup.item.quantity !== current.item.quantity;
  }

  /**
   * all "live" items - active plus excluded-from-pricing - in no particular order. These are the
   * items that are displayed, groupable and carried in itemDisplayOrder. Deleted items are not
   * live. Excluded items are held in their own partition but are still part of the quote layout.
   */
  liveItems(): QuoteItem[] {
    return [...(this.container.items ?? []), ...(this.container.excludedItems ?? [])];
  }

  /**
   * Re-apply a sorted live-item list to the active and excluded partitions, preserving the
   * existing partition MEMBERSHIP (by id) and only reordering within it. Moving an item between
   * partitions is done by repartitionByState (which moves the item together with its
   * price/data/buy-in); sorting must not split by state alone, or an item could move lists while
   * its price is left behind.
   */
  private applySortedLiveItems(sorted: QuoteItem[]) {
    const excludedIds = new Set((this.container.excludedItems ?? []).map(x => x.id));
    this.container.items = sorted.filter(x => !excludedIds.has(x.id));
    this.container.excludedItems = sorted.filter(x => excludedIds.has(x.id));
  }

  async performItemSorting() {
    if (!this.container.items) await this.needsQuoteItems();
    else this.applySortedLiveItems(await this.internalSortItemsAsList());
  }

  /**
   * Updates the sort order of items in the container's quote presentation. The PRE-GROUPING order path: it
   * writes itemDisplayOrder directly and is only correct while no grouping document exists, in which case
   * the order is the truth and any document built later is built FROM it.
   *
   * Refuses once a document is loaded, rather than writing an order the document would silently overwrite on
   * the next save. The document is the source of truth for order (see quote-item-grouping.ts); a caller that
   * wants to reorder a grouped quote must go through applyItemGrouping so both move together. This guard is
   * here because the same mistake in the membership path - writing one side of the pair and leaving the
   * document behind - dropped per-batch supplier charges out to quote level, silently and permanently.
   *
   * @param {string[]} sortedOrder - The new sorted order of items.
   * @returns {Promise<boolean>} - A promise that resolves to true if the sort order was updated successfully, and false otherwise.
   */
  async updateItemSortOrder(sortedOrder: string[]) {
    if (!this.container.quotePresentation) return false;

    if (this.itemGrouping) {
      await showDevelopmentError('updateItemSortOrder called on a grouped quote - reorder via applyItemGrouping');
      return false;
    }

    if (this.container.quotePresentation.itemDisplayOrder.length !== sortedOrder.length) return false;

    const sorted1 = this.container.quotePresentation.itemDisplayOrder.slice().sort();
    const sorted2 = sortedOrder.slice().sort();

    for (let i = 0; i < sorted1.length; i++) {
      if (sorted1[i] !== sorted2[i]) {
        return false;
      }
    }

    this.container.quotePresentation.itemDisplayOrder = sortedOrder;
    const saved = await this.saveQuote();
    if (saved) await this.performItemSorting();
    return saved;
  }

  /**
   * the current quote item grouping document, or null when not yet loaded via needsItemGrouping.
   */
  public get quoteItemGrouping(): QuoteItemGroupingDocument | null {
    return this.itemGrouping;
  }

  /**
   * True when a batch/grouping edit deferred the per-batch SSI recompute (Task 230006, §6): the
   * supplier-computed (SSI) prices are stale and the quote must not be issued until the user runs
   * Update Price (which clears the flag and re-runs SSI). Backed by the server-visible Quote.ssiDeferred
   * column.
   */
  public get ssiDeferred(): boolean {
    return this.quote.ssiDeferred === true;
  }

  /**
   * The batch a quote item belongs to (by commonId), or null when ungrouped. Membership lives in the
   * QuoteItemBatchLink table (loaded into itemBatchMembership), never on the item row — so this is the
   * source the SSI reconcile keys off.
   */
  public itemBatchId(commonId: string): string | null {
    return this.itemBatchMembership.get(commonId) ?? null;
  }

  /**
   * commonId → the number shown against each line. THE one source of item numbering: the items table, the
   * purchase order it records at creation, and (mirrored) both reports all resolve to this.
   *
   * Numbering is "your position within your container", so this delegates straight to the document, which
   * already holds every line in its final position - charges included. See computeBatchItemNumbers for the
   * convention. It previously had to walk the flat presentation order and resolve each line's container from
   * the link map, and that is precisely how the table and the reports came to disagree about which section a
   * batch's own shipping line belonged to.
   */
  public itemDisplayNumbers(): Map<string, string> {
    if (this.itemGrouping) return computeBatchItemNumbers(this.itemGrouping);

    // Document not loaded yet (a render that beat needsItemGrouping). With no groups known the quote is one
    // implicit container, which is what the numbering rule reduces to anyway: flat 1..N over the
    // presentation order. Callers that must not guess - the purchase order records these numbers
    // permanently - load the document first.
    const numbers = new Map<string, string>();
    let position = 0;
    for (const commonId of this.container.quotePresentation?.itemDisplayOrder ?? []) {
      position += 1;
      numbers.set(commonId, String(position));
    }
    return numbers;
  }

  /**
   * Record an item's batch membership in memory. A null / empty value clears it. Persistence happens via
   * the batch assignments on the next quote save.
   *
   * Written into the grouping DOCUMENT as well as the link map, and that is not redundant: the save derives
   * the links from the document, so a membership recorded only in the map is one the next save deletes. This
   * is the path a per-batch supplier charge arrives by (the SSI run decides its batch), and leaving the
   * document behind dropped those charges out to quote level on the very next save.
   */
  public setItemBatch(commonId: string, batchId: string | null): void {
    const target = batchId && batchId !== emptyGuid ? batchId : null;
    if (target) this.itemBatchMembership.set(commonId, target);
    else this.itemBatchMembership.delete(commonId);

    if (this.itemGrouping) {
      this.itemGrouping = assignItemToGroup(this.itemGrouping, commonId, target, this.itemPlacementRules()).doc;
    }
  }

  /**
   * Batch membership staged against the item **id** rather than its commonId (Task 230006), as a CORRECTION
   * path — not because the commonId is unknown.
   *
   * It used to be unknown: the server minted `CommonId` on insert, so a brand-new charge had no document
   * identity until the insert came back. `TaskCreateQuoteItem` now sets `CommonId = Id` and the client mints
   * the id, so the charge is placed by commonId up front (`setItemBatch` in v6-ssi-processor) and rendered in
   * its batch immediately, with no two-phase.
   *
   * This is kept for the case that assumption fails: if an insert ever returns a CommonId other than the id we
   * minted — an older server in a rolling deploy, a task that re-mints — the placement above is keyed on an
   * identity that does not exist, and {@link resolvePendingItemBatch} re-places the line under the real one.
   * Deliberately belt-and-braces on the single field whose loss silently drops a charge out of its batch.
   */
  private pendingItemBatchByItemId = new Map<string, string | null>();

  public stageItemBatchByItemId(itemId: string, batchId: string | null): void {
    this.pendingItemBatchByItemId.set(itemId, batchId && batchId !== emptyGuid ? batchId : null);
  }

  /**
   * Translate any staged id→batch membership into the commonId space now that the inserted rows are back
   * from the server. Items that did not come back are dropped (the insert failed or was superseded).
   */
  protected resolvePendingItemBatches(): void {
    if (this.pendingItemBatchByItemId.size === 0) return;
    for (const [itemId, batchId] of this.pendingItemBatchByItemId) {
      const item = this.container.items?.find(x => x.id === itemId);
      if (item) this.setItemBatch(item.commonId, batchId);
    }
    this.pendingItemBatchByItemId.clear();
  }

  /**
   * Resolve ONE inserted row's staged membership against the identity the server actually returned. Called
   * from processResultItem so the placement is in the document before it sorts the list, which is what stops
   * a per-batch charge painting at the bottom of the quote and then jumping into its batch.
   *
   * Normally a no-op now: `CommonId = Id` at creation means the SSI processor already placed the charge under
   * the identity the server will return, so this re-places it under the same value. It earns its keep only when
   * the returned CommonId differs from the minted id, where it is the difference between a charge sitting in its
   * batch and silently falling out to quote level. See {@link pendingItemBatchByItemId}.
   */
  protected resolvePendingItemBatch(item: QuoteItem): void {
    if (!this.pendingItemBatchByItemId.has(item.id)) return;
    const batchId = this.pendingItemBatchByItemId.get(item.id) ?? null;
    this.pendingItemBatchByItemId.delete(item.id);
    this.setItemBatch(item.commonId, batchId);
  }

  /**
   * Derive the batch inputs to send with a quote save from the current grouping document (Task 230006):
   * the batches (groups + their authored metadata) and the per-item assignments (every groupable item →
   * its group id, or emptyGuid when ungrouped so stale links are removed server-side). Returns nulls when
   * grouping isn't loaded, so the server leaves batches untouched on non-grouping saves.
   *
   * This is also where an unresolved quote converges: a grouping document whose groups have no batch rows
   * yet (grouping shipped before batches) is not migrated separately — the batches are simply derived from
   * the document like any other save, so the first save that carries grouping materializes them. The
   * load-time heal deliberately leaves such groups alone so they survive to reach this point.
   *
   * Assignments cover every item in the document, from that ONE source. The document holds the charge lines
   * as well now, each in the container the supplier put it in, so a charge's link row is written from the
   * same place as everything else's. This used to need a second pass over {@link itemBatchMembership},
   * because charges were absent from the document - two sources feeding one column, and the server could
   * only validate the half of it that the document described.
   */
  protected buildBatchSaveInputs(): {
    batches: QuoteItemBatchInput[] | null;
    itemBatchAssignments: { [key: string]: string } | null;
  } {
    const doc = this.itemGrouping;
    if (!doc) return { batches: null, itemBatchAssignments: null };

    // The document exists but is not authoritative - it was invented after the blob failed to load, so it has
    // no groups and reporting it would delete every batch row. Null means "leave batches alone", which is
    // exactly what we can honestly say here. The batches the dealer authored are still in the DB and the
    // load-time heal restores them to the document on the next successful load.
    if (this.itemGroupingUnavailable) return { batches: null, itemBatchAssignments: null };

    const batches: QuoteItemBatchInput[] = getGroups(doc).map(g => ({
      // The group node id IS the batch CommonId - the server upserts on it and re-keys PKs on copy.
      commonId: g.id,
      description: g.name,
      excludeFromPricing: g.attributes.excludeFromPricing === true,
      deliveryDate: normalizeBatchDate(g.attributes.deliveryDate),
      customerRef: (g.attributes.customerRef as string | null) ?? null,
      status: (g.attributes.status as string | null) ?? null,
      comment: (g.attributes.comment as string | null) ?? null,
      isPrimary: g.attributes[primaryBatchAttributeKey] === true
    }));

    // emptyGuid is the ungroup sentinel (nswag drops null dictionary values), so a trunk-level line is sent
    // explicitly as "no batch" rather than omitted. A charge whose batch was deleted is back at the trunk by
    // then, so it lands here as emptyGuid too and the server drops the stale link.
    const membership = batchMembershipMap(doc);
    const itemBatchAssignments: { [key: string]: string } = {};
    for (const commonId of allItemIds(doc)) {
      itemBatchAssignments[commonId] = membership.get(commonId) ?? emptyGuid;
    }

    return { batches, itemBatchAssignments };
  }

  /**
   * "Update Price" recovery for the deferred-SSI flow (Task 230006, §6): re-run the supplier-side items
   * (SSI) + reprice, then clear the SsiDeferred flag and persist. After this, pricing is current again and
   * the issue gate lifts. No-op-safe for suppliers that don't use SSI.
   */
  public async updatePrice(): Promise<void> {
    await this.runAndSaveSSI();
    this.quote.ssiDeferred = false;
    await this.saveQuote(true);
  }

  /**
   * commonIds of the items the DEALER MAY POSITION: the live, user-sortable ones. Both active and
   * excluded-from-pricing items qualify (an excluded item lives inside its group); the charge lines and the
   * deleted items do not.
   *
   * This is no longer the same thing as "in the grouping document" - the document holds every live item,
   * charges included. This set is about MOVABILITY, and it is what the authoring derivations key off: which
   * items a regroup could have moved, and which items an exclude-from-pricing flag may restate.
   */
  protected groupableCommonIds(): Set<string> {
    return new Set(
      this.liveItems()
        .filter(x => !this.itemIsNotSortable(x))
        .map(x => x.commonId)
    );
  }

  /**
   * Placement rules for every operation that writes the grouping document: which items it should hold (all
   * the live ones) and which of those the dealer cannot position (the charge lines, ranked SSI → shipping →
   * price adjustment - the order they have always appeared in at the bottom of a quote).
   */
  protected itemPlacementRules(): ItemPlacementRules {
    const live = this.liveItems();
    const rankByCommonId = new Map<string, number>();
    for (const item of live) {
      if (!this.itemIsNotSortable(item)) continue;
      rankByCommonId.set(item.commonId, isShipping(item) ? 1 : isSSI(item) ? 0 : 2);
    }
    return {
      items: new Set(live.map(x => x.commonId)),
      forcedRank: commonId => rankByCommonId.get(commonId) ?? null
    };
  }

  /**
   * this should be called before accessing the grouping document. Loads the "itemGrouping" quote
   * asset; when missing or invalid, a fresh document is built from the legacy
   * quotePresentation.itemDisplayOrder (migration fallback). The result is reconciled against the
   * live item list and kept in memory only - nothing is persisted until the first user-initiated
   * grouping mutation (applyItemGrouping), so read-only views never write.
   */
  public needsItemGrouping = sharedExecute(this._needsItemGrouping.bind(this));
  private async _needsItemGrouping(): Promise<QuoteItemGroupingDocument> {
    await this.needsQuote();
    await this.needsQuoteItems();
    const rules = this.itemPlacementRules();
    if (!this.itemGrouping) {
      let doc: QuoteItemGroupingDocument | null = null;
      const assetInfo = this.quote.assetVirtualPaths?.[quoteItemGroupingAssetKey];
      if (assetInfo?.path) {
        try {
          const json = await fetchQuoteAsset({
            quoteId: this.quoteId,
            assetPath: assetInfo.path,
            publicUrl: assetInfo.publicUrl
          });
          doc = parseGroupingDocument(json as string | null);
        } catch (error) {
          console.warn(`Unable to load quote item grouping asset for quote ${this.quoteId}`, error);
        }
      }
      // Is the document we are about to use AUTHORITATIVE about batches, or invented?
      //
      // Two very different situations both end in "fall back to itemDisplayOrder", and only one of them is
      // safe to save batches from:
      //   * no asset path at all - no grouping blob has ever been written, so the fallback IS the truth and
      //     there are no batch rows to lose; and
      //   * a path exists but the fetch or the parse failed - a transient. The invented document has no
      //     groups, and saving from it would report "no batches" and delete every batch row on the quote.
      // Only the second case is suppressed. See buildBatchSaveInputs; the server refuses this outright too
      // (QuoteItemBatchWriter.ApplyAsync), because a client should not be the only thing standing between a
      // dropped network request and the dealer's batching.
      this.itemGroupingUnavailable = !!assetInfo?.path && doc === null;
      this.itemGrouping =
        doc ?? buildGroupingFromDisplayOrder(this.container.quotePresentation?.itemDisplayOrder ?? [], rules);
    }

    // Heal the presentation blob against the DB batch state (Task 230006, §3.3b): refresh batch metadata
    // from the DB (DB wins), add detached batches, drop groups whose batch was deleted, and correct item
    // membership from the link table.
    //
    // This heal is IN MEMORY ONLY and must stay that way. It deliberately does NOT save.
    //
    // It used to persist immediately, which produced two bad failures on a copied quote (both observed):
    //   * an infinite write loop - the save reloads the items summary, which re-arms batchSnapshotFresh,
    //     which heals again; if the blob and DB never converge byte-for-byte that never terminates; and
    //   * a blank items table - refreshData() awaits needsItemGrouping() and builds its columns after it,
    //     with no catch, so any rejected save aborted the whole render.
    // A copy is exactly where convergence cannot be assumed: the batch rows commit inside the duplicate
    // transaction but the itemGrouping blob is copied asynchronously afterwards, so for a window the DB is
    // legitimately ahead of the blob and every heal "changes" something.
    //
    // Correctness is unaffected. The DB is the source of truth for batch existence and metadata; the blob is
    // presentation. Healing in memory means the user always sees DB truth, and any grouping mutation
    // (applyItemGrouping) rewrites the whole document anyway - so the heal never needs to write, and must
    // not stage either: staging would leave a freshly-opened quote looking edited before the user touched it.
    //
    // ONLY run this while the batch snapshot is fresh from the server (batchSnapshotFresh). needsItemGrouping
    // is also called at the start of every grouping mutation (createItemGroup / moveItemToGroup / ...), but
    // those do not reload the summary - so between saves the snapshot is stale. Reconciling then would see
    // the batch we just created as "missing from the DB" and delete the group (and its DB row) again, so a
    // group could never stick. Consuming the flag here restricts the heal to genuine (re)loads.
    if (this.itemGrouping && this.batchSnapshotFresh) {
      this.batchSnapshotFresh = false;
      try {
        const dbBatchRows: QuoteItemBatchRow[] = this.quoteItemBatches.map(b => ({
          // The blob's group node id is the batch COMMON id (stable across quote copies), never the row PK.
          id: b.commonId,
          description: b.description,
          excludeFromPricing: b.excludeFromPricing,
          deliveryDate: b.deliveryDate,
          customerRef: b.customerRef,
          comment: b.comment,
          status: b.status,
          isPrimary: b.isPrimary
        }));
        const healed = reconcileBatchesIntoGrouping(this.itemGrouping, dbBatchRows, this.itemBatchMembership, rules);
        if (healed.changed) {
          this.itemGrouping = healed.doc;
          this.itemGroupingHealedUnsaved = true;
        }
      } catch (error) {
        // Housekeeping must never take the items view down - see the blank-table failure above. Keep the
        // document as loaded and carry on; the next genuine load or save reconciles again.
        console.warn(`Unable to reconcile quote item batches for quote ${this.quoteId}`, error);
      }
    }

    return this.reconcileItemGrouping();
  }

  /**
   * Flushes a load-time heal (see {@link itemGroupingHealedUnsaved}) to the saved blob, so that anything
   * reading the quote's PERSISTED grouping - a server-rendered report, a purchase order snapshot - sees the
   * same groups and numbering the items view does. A no-op when there is nothing to flush (the common case),
   * so a quote already in sync writes nothing extra.
   *
   * Unlike the heal itself, this call is driven by the caller (a deliberate action - generating a report),
   * never by a render or a load, which is what the heal's own comment forbids: see the infinite-write-loop
   * and blank-items-table failures recorded on {@link _needsItemGrouping}. One explicit flush before one
   * report request cannot recreate either - it does not re-run on every render, and a rejected save here
   * only fails that one report request rather than the page.
   */
  public async persistHealedItemGrouping(): Promise<boolean> {
    await this.needsItemGrouping();
    if (!this.itemGroupingHealedUnsaved || !this.itemGrouping || this.itemGroupingUnavailable) return true;

    const saved = await this.applyItemGrouping(this.itemGrouping, true);
    if (saved) this.itemGroupingHealedUnsaved = false;
    return saved;
  }

  /**
   * cheap idempotent re-sync of the grouping document against the live item list; picks up items
   * added or removed server-side (create/copy/delete/price adjustment). Safe to call before every
   * render. Requires needsItemGrouping to have run at least once.
   *
   * Like the DB-batch heal above, this correction is IN MEMORY ONLY and must stay that way (the same
   * infinite-write-loop / blank-items-table failures apply). A newly-added item defaulting into the
   * primary batch is exactly this kind of correction, so it marks {@link itemGroupingHealedUnsaved} too -
   * otherwise a report generated before the next explicit grouping edit would read the stale saved blob
   * and render the new item ungrouped at the tail (bug 231259).
   */
  public reconcileItemGrouping(): QuoteItemGroupingDocument {
    if (!this.itemGrouping) throw new DevelopmentError('needsItemGrouping must be called before reconcileItemGrouping');
    const { doc, changed } = reconcileGrouping(
      this.itemGrouping,
      this.itemPlacementRules(),
      this.container.quotePresentation?.itemDisplayOrder ?? []
    );
    this.itemGrouping = doc;
    if (changed) this.itemGroupingHealedUnsaved = true;
    return doc;
  }

  /**
   * the single write path for grouping changes. Normalizes the document (see normalizeGroupingDocument for
   * the layout rules it applies), validates that it covers exactly the live items, dual-writes the flattened
   * order into quotePresentation.itemDisplayOrder, and registers the serialized document as a pending quote
   * asset before saving.
   *
   * The flatten IS the presentation order: the document holds every item, charges included and already in
   * position, so itemDisplayOrder is a faithful projection of it rather than the document plus a separately
   * re-appended tail of forced lines.
   */
  public async applyItemGrouping(doc: QuoteItemGroupingDocument, save = true): Promise<boolean> {
    if (!this.container.quotePresentation) return false;

    const rules = this.itemPlacementRules();
    doc = normalizeGroupingDocument(doc, rules).doc;

    const flattened = flattenGroupingToDisplayOrder(doc);
    if (flattened.length !== rules.items.size || flattened.some(id => !rules.items.has(id))) {
      await showDevelopmentError('Grouping document does not match the current quote items');
      return false;
    }

    const previousDoc = this.itemGrouping;
    const previousOrder = this.container.quotePresentation.itemDisplayOrder;
    const previousAsset = this.quoteUpdateAssets?.[quoteItemGroupingAssetKey] ?? null;

    // Did any item move into / out of / between batches (or a batched item get removed)? If so, every
    // per-batch SSI (shipping/supplier charge) is now stale and must be deferred (Task 230006, §6).
    // Only when the supplier's V6 links groups to batches - otherwise groups never reach V6, so no
    // regroup can invalidate a price and deferring would strand the quote behind an Update Price it
    // does not need.
    const batchCompositionChanged =
      save &&
      this.isV6BatchLinkingEnabled &&
      batchMembershipChanged(previousDoc ?? createEmptyGroupingDocument(), doc, rules);

    // The FIRST batching of a quote is not deferred. Deferral exists to avoid a recompute storm while the
    // user shuffles items between batches, but going from no batches to batched happens exactly once, and
    // it is the moment every SSI line has to be re-derived anyway (a quote-level charge may split into
    // per-batch ones). Running it now leaves the quote priced and its charges sitting in the right place
    // from the outset, instead of parking it behind an Update Price the user never asked for and showing a
    // layout that rearranges itself once they press it.
    const firstBatching =
      batchCompositionChanged &&
      getGroups(previousDoc ?? createEmptyGroupingDocument()).length === 0 &&
      getGroups(doc).length > 0;

    this.itemGrouping = doc;

    // materialize the per-item exclude-from-pricing state from the doc's group flags. This is a
    // separate item-update round-trip that recomputes pricing; do it before the grouping save so a
    // failure aborts cleanly without writing the grouping asset.
    let pricingInclusionChanged = false;
    if (save) {
      const exclusionResult = await this.applyExcludeFromPricingStates(doc);
      if (!exclusionResult.ok) {
        this.itemGrouping = previousDoc;
        return false;
      }
      pricingInclusionChanged = exclusionResult.changed;
    }

    // This save writes a real grouping blob, so whatever could not be read before is about to be replaced by
    // something we authored - the document is authoritative again from here, and any outstanding in-memory
    // heal is now reflected in it too.
    this.itemGroupingUnavailable = false;
    this.itemGroupingHealedUnsaved = false;

    this.container.quotePresentation.itemDisplayOrder = flattened;
    await this.performItemSorting();
    this.updatePendingQuoteAssets({
      [quoteItemGroupingAssetKey]: {
        assetPath: this.api.createQuoteAssetPath(quoteItemGroupingAssetKey, '.json'),
        data: serializeGroupingDocument(doc),
        dataBase64: null,
        delete: false,
        hasContent: false
      }
    });

    if (!save) return true;

    // Batch composition changed -> defer the per-batch SSI recompute. Flag the quote (persisted with this
    // same save) so every SSI line renders as "pricing unavailable" until the user runs Update Price; do
    // NOT run SSI synchronously here (deferral is the whole point - avoids an N-move recompute storm).
    const previousSsiDeferred = this.quote.ssiDeferred;
    if (batchCompositionChanged) {
      this.quote.ssiDeferred = true;
    }

    const saved = await this.saveQuote();
    if (!saved) {
      this.itemGrouping = previousDoc;
      this.container.quotePresentation.itemDisplayOrder = previousOrder;
      await this.performItemSorting();
      this.updatePendingQuoteAssets({ [quoteItemGroupingAssetKey]: previousAsset });
      // The grouping change never landed, so nothing invalidated the pricing - roll the flag back with the
      // rest of it, or the user is stranded behind an Update Price for an edit that did not happen.
      this.quote.ssiDeferred = previousSsiDeferred;
      return saved;
    }

    if (firstBatching) {
      // The flag was still set before the save above, on purpose: if this recompute fails the quote stays
      // marked stale and the user keeps the Update Price affordance, rather than being left with silently
      // wrong pricing. updatePrice() is exactly that recovery - it re-runs SSI, clears the flag and saves.
      try {
        await this.updatePrice();
      } catch (e) {
        await showDevelopmentError(`Failed to update supplier items after the first batch was created: ${e}`);
      }
    } else if (batchCompositionChanged) {
      // Deferred: SSI is intentionally NOT re-run now. Update Price clears ssiDeferred and reruns it.
    } else if (pricingInclusionChanged) {
      // a pricing-inclusion change alters the active item set, so the supplier-generated (SSI) items
      // - shipping, supplier specials, etc. - may need to be created or removed. Re-run the V6 SSI
      // flow against the new active set (excluded items already sit outside container.items, so the
      // SSI contract omits them). This is the same V6 round-trip the defaults "apply to all items"
      // action performs, and it is a no-op for suppliers that do not use SSI.
      // The grouping change is already persisted, so an SSI failure must not reject it - surface it
      // without rolling back (the SSI items reconcile on the next save/validate).
      try {
        await this.runAndSaveSSI();
      } catch (e) {
        await showDevelopmentError(`Failed to update supplier items after a pricing-inclusion change: ${e}`);
      }
    }

    return saved;
  }

  /**
   * Flip each groupable item's state between Active and ExcludeFromPricing to match the doc's group
   * `excludeFromPricing` flags, persisting the changes in one item-update batch (which recomputes
   * the Active-only quote price server-side) and re-partitioning the local active/excluded lists.
   * Items already in the correct state are skipped. Returns `ok` (did the persist succeed) and
   * `changed` (did any item actually transition) - the caller uses `changed` to decide whether the
   * supplier-generated (SSI) items need regenerating. With no transitions this is a no-op.
   */
  private async applyExcludeFromPricingStates(
    doc: QuoteItemGroupingDocument
  ): Promise<{ ok: boolean; changed: boolean }> {
    const rules = this.itemPlacementRules();
    const excluded = excludedFromPricingCommonIds(doc, rules);
    const liveByCommon = new Map(this.liveItems().map(i => [i.commonId, i]));

    const batch: UpdateQuoteItem[] = [];
    for (const commonId of allItemIds(doc)) {
      // A charge line is IN the document but its state is not ours to write - that belongs to the SSI run
      // which produced it. Without this skip, holding charges in the document would make every grouping save
      // assert Active over whatever the supplier had set.
      if (rules.forcedRank(commonId) !== null) continue;

      const item = liveByCommon.get(commonId);
      if (!item) continue;
      const target = excluded.has(commonId) ? QuoteItemState.ExcludeFromPricing : QuoteItemState.Active;
      if (item.state === target) continue;
      const container = this.quoteItemContainer(item.id);
      // build the update from a clone carrying the target state so the live item is untouched until
      // the server confirms; the price is included so the server recomputes the quote total.
      const inputContainer: QuoteItemContainer = { ...container, item: { ...container.item, state: target } };
      batch.push({
        action: QuoteItemRowAction.Update,
        input: this.quoteItemContainerAsUpdateItemInput({}, inputContainer)
      });
    }

    if (batch.length === 0) return { ok: true, changed: false };

    const ok = await this.procesItemUpdateBatch(null, QuoteItemRowAction.Update, { items: batch });
    if (ok) this.repartitionByState();
    return { ok, changed: ok };
  }

  /**
   * Create a batch. The FIRST batch on a quote also brings the primary catch-all into existence
   * (Task 230006): once any batching exists no user-sortable item sits loose, so every ungrouped item moves
   * into the primary. Both groups and the sweep are one document mutation and therefore one save — the quote
   * goes from "unbatched" to "fully batched" atomically, never through a half-batched state.
   *
   * The quote-level charges stay where they are: which container a charge belongs to is the supplier's
   * answer, not a consequence of the dealer creating a batch.
   *
   * @param primaryBatchName localized name for the auto-created catch-all. Omit only where no primary
   *   should be created (there is currently no such caller; it is required for batching to be coherent).
   * @param moveCommonId an item to place in the new group as part of the SAME document mutation.
   *   "Create a group containing this item" is ONE user intent and must be one save: done as two
   *   (create, then move) the first save saw no-groups → groups and ran SSI immediately as intended, and
   *   the second save then saw a composition change on an already-batched quote and deferred it straight
   *   back again - so the seamless first transition was real but instantly undone.
   */
  public async createItemGroup(
    name: string,
    attributes?: Partial<QuoteItemGroupAttributes>,
    primaryBatchName?: string,
    moveCommonId?: string
  ): Promise<string | null> {
    const doc = await this.needsItemGrouping();
    let working = doc;
    if (primaryBatchName && !findPrimaryGroup(working)) {
      // At trunk index 0 so the catch-all reads as the default section above the batches carved out of it.
      // normalizeGroupingDocument then sweeps every loose sortable item into it.
      working = createGroup(working, primaryBatchName, { [primaryBatchAttributeKey]: true }, 0).doc;
    }

    const created = createGroup(working, name, attributes);
    const placed = moveCommonId
      ? moveItem(created.doc, moveCommonId, { groupId: created.groupId, index: 0 })
      : created.doc;

    return (await this.applyItemGrouping(placed)) ? created.groupId : null;
  }

  /** update a group's name and attributes in a single save */
  public async updateItemGroup(
    groupId: string,
    name: string,
    attributes: Partial<QuoteItemGroupAttributes>
  ): Promise<boolean> {
    const doc = await this.needsItemGrouping();
    return await this.applyItemGrouping(setGroupAttributes(renameGroup(doc, groupId, name), groupId, attributes));
  }

  /** delete a group; its items return to the trunk at the group's position */
  public async deleteItemGroup(groupId: string): Promise<boolean> {
    const doc = await this.needsItemGrouping();
    return await this.applyItemGrouping(deleteGroup(doc, groupId));
  }

  /** move an item into a group (or the trunk when groupId is null) at the given index */
  public async moveItemToGroup(commonId: string, groupId: string | null, index: number): Promise<boolean> {
    const doc = await this.needsItemGrouping();
    return await this.applyItemGrouping(moveItem(doc, commonId, { groupId, index }));
  }

  /** move a group (with its items) to a new trunk position */
  public async moveItemGroup(groupId: string, trunkIndex: number): Promise<boolean> {
    const doc = await this.needsItemGrouping();
    return await this.applyItemGrouping(moveGroup(doc, groupId, trunkIndex));
  }

  quoteAttributesRequireSSIRecalculation(): boolean {
    return false;
  }
  anyFrameOutOfDate(_errors: string[]): Promise<boolean> {
    return Promise.resolve(false);
  }

  updateQuoteTermsAndConditions(terms: string | null | undefined) {
    if (this.container.quote) this.container.quote.termsAndConditions = terms ?? null;
  }

  /**
   * this should be called any time before accessing the quote items list, which may yet be unpopulated.
   * calling this makes all references to data invalid so it should not be called while there are active
   * items being used or edited.
   * @param forceRefresh force a reload of the data even if not required.
   * @returns true if suceeded
   */

  public needsQuoteItems = sharedExecute(this._needsQuoteItems.bind(this));
  private async _needsQuoteItems(forceRefresh = false): Promise<boolean> {
    const hydrateMissingProviderData = async (result: ResultGetQuoteItemsSummary) => {
      await Promise.all(result.data.map(item => this.hydrateQuoteItemProviderData(item)));
    };

    if (!this.container.items || forceRefresh) {
      this.itemContainerCache = [];
      this.itemContainerCacheBak = [];
      const result = await this.api.getQuoteItemsSummary({
        quoteId: this.quoteId,
        includeAllStates: true
      });

      if (result) {
        await hydrateMissingProviderData(result);
        const sortedItems: QuoteItem[] = await this.internalSortItemsAsList(result.items);

        // Classify every item by state into one of three partitions. "active" = priced and
        // surfaced everywhere; "excluded" = kept and grouped but not priced/exported; "deleted".
        type Partition = 'active' | 'excluded' | 'deleted';
        const partitionOf = new Map<string, Partition>();
        for (const item of sortedItems) {
          partitionOf.set(
            item.id,
            isDeletedQuoteItemState(item)
              ? 'deleted'
              : isExcludedFromPricingQuoteItemState(item)
                ? 'excluded'
                : 'active'
          );
        }
        const partForId = (id: string): Partition => partitionOf.get(id) ?? 'active';

        this.container.items = sortedItems.filter(x => partForId(x.id) === 'active');
        this.container.excludedItems = sortedItems.filter(x => partForId(x.id) === 'excluded');
        this.container.deletedItems = sortedItems.filter(x => partForId(x.id) === 'deleted');
        // Partition associated data sets the same three ways to avoid scanning later.
        this.container.itemsData = result.data.filter(d => partForId(d.id) === 'active');
        this.container.excludedItemsData = result.data.filter(d => partForId(d.id) === 'excluded');
        this.container.deletedItemsData = result.data.filter(d => partForId(d.id) === 'deleted');
        this.container.itemsBuyInData = result.quoteItemBuyInData.filter(d => partForId(d.id) === 'active');
        this.container.excludedItemsBuyInData = result.quoteItemBuyInData.filter(d => partForId(d.id) === 'excluded');
        this.container.deletedItemsBuyInData = result.quoteItemBuyInData.filter(d => partForId(d.id) === 'deleted');
        this.container.itemPrices = result.prices.filter(p => partForId(p.id) === 'active');
        this.container.excludedItemPrices = result.prices.filter(p => partForId(p.id) === 'excluded');
        this.container.deletedItemPrices = result.prices.filter(p => partForId(p.id) === 'deleted');

        // Batch state (Task 230006): the DB batches + the item→batch link mapping. The grouping blob is
        // reconciled against these on needsItemGrouping; the SSI reconcile keys off the membership map.
        this.quoteItemBatches = result.batches ?? [];
        // Link rows carry the batch PK; translate to the batch CommonId so the membership map is in the
        // same identity space as the grouping blob and the V6 contract.
        const batchCommonIdByPk = new Map((result.batches ?? []).map(b => [b.id, b.commonId]));
        this.itemBatchMembership = new Map(
          (result.itemBatchLinks ?? [])
            .map(l => [l.quoteItemCommonId, batchCommonIdByPk.get(l.quoteItemBatchId)])
            .filter((entry): entry is [string, string] => !!entry[1])
        );
        // A fresh DB batch snapshot just arrived - allow the load-time blob heal to run once against it.
        this.batchSnapshotFresh = true;

        await this.validateBuyInPrices();

        this.backup.items = this.clone(this.container.items);
        this.backup.excludedItems = this.clone(this.container.excludedItems);
        this.backup.deletedItems = this.clone(this.container.deletedItems);
        this.backup.itemsData = this.clone(this.container.itemsData);
        this.backup.excludedItemsData = this.clone(this.container.excludedItemsData);
        this.backup.deletedItemsData = this.clone(this.container.deletedItemsData);
        this.backup.itemsBuyInData = this.clone(this.container.itemsBuyInData);
        this.backup.excludedItemsBuyInData = this.clone(this.container.excludedItemsBuyInData);
        this.backup.deletedItemsBuyInData = this.clone(this.container.deletedItemsBuyInData);
        this.backup.itemPrices = this.clone(this.container.itemPrices);
        this.backup.excludedItemPrices = this.clone(this.container.excludedItemPrices);
        this.backup.deletedItemPrices = this.clone(this.container.deletedItemPrices);
      } else return false;
    }
    return true;
  }

  public async hydrateQuoteItemProviderData(item: QuoteItemProviderData | null): Promise<void> {
    if (!item) return;
    if (item.providerData && item.providerData !== '') return;
    const dataPath = item.assetVirtualPaths?.['data'];
    if (!dataPath?.path) return;

    try {
      const providerData = await fetchQuoteAsset({
        quoteId: this.quoteId,
        quoteItemId: item.id,
        assetPath: dataPath.path,
        publicUrl: dataPath.publicUrl
      });
      item.providerData = providerData as string;
    } catch (error) {
      console.warn(
        `Falling back to legacy fetch for provider data for quote item ${item?.id} from path ${dataPath?.path}`,
        error
      );
    }
  }

  prepareForUnDelete(quoteItemId: string) {
    if (this.moveItemPartition(quoteItemId, 'deleted', 'active')) {
      return this.quoteItemContainer(quoteItemId);
    }
    return undefined;
  }

  /** field names of the four parallel arrays backing each item partition */
  private static partitionFields(partition: ItemPartition) {
    switch (partition) {
      case 'active':
        return { items: 'items', data: 'itemsData', buyIn: 'itemsBuyInData', prices: 'itemPrices' } as const;
      case 'excluded':
        return {
          items: 'excludedItems',
          data: 'excludedItemsData',
          buyIn: 'excludedItemsBuyInData',
          prices: 'excludedItemPrices'
        } as const;
      case 'deleted':
        return {
          items: 'deletedItems',
          data: 'deletedItemsData',
          buyIn: 'deletedItemsBuyInData',
          prices: 'deletedItemPrices'
        } as const;
    }
  }

  /**
   * Move an item and its associated provider/buy-in/price records from one partition to another,
   * keeping the active/excluded/deleted lists consistent. Removes the container cache entry so the
   * next lookup rebuilds from the new partition. Returns false when the item is not in `from`.
   * The item's own `state` field is the caller's responsibility (it must already reflect `to`).
   */
  private moveItemPartition(quoteItemId: string, from: ItemPartition, to: ItemPartition): boolean {
    if (from === to) return false;
    const fromF = QuoteContainerManager.partitionFields(from);
    const toF = QuoteContainerManager.partitionFields(to);
    const c = this.container as unknown as Record<string, unknown[] | null | undefined>;

    const itemIdx = (c[fromF.items] ?? []).findIndex((x: any) => x.id === quoteItemId);
    if (itemIdx < 0) return false;

    const moveOne = (fromKey: string, toKey: string) => {
      const src = c[fromKey] as any[] | null | undefined;
      if (!src) return;
      const idx = src.findIndex(x => x.id === quoteItemId);
      if (idx < 0) return;
      const moved = src.splice(idx, 1)[0];
      (c[toKey] ??= []).push(moved);
    };
    moveOne(fromF.items, toF.items);
    moveOne(fromF.data, toF.data);
    moveOne(fromF.buyIn, toF.buyIn);
    moveOne(fromF.prices, toF.prices);

    this.removeContainerCache(quoteItemId);
    return true;
  }
  saveQuoteCalculations(_v: QCQuoteValues | null | undefined) {
    //override this method to save the quote calculations
  }

  saveQuoteAnnotations(_v: V6QuoteAnnotation[] | null | undefined) {
    //override this method to save the quote annotations
  }

  quoteProviderData(): QuoteProviderData | null {
    return this.container.quoteProviderData;
  }

  quoteItemContainerCache(quoteItemId: string, useBackup = false): QuoteItemContainer {
    const cache = useBackup ? this.itemContainerCacheBak : this.itemContainerCache;
    let result = cache.find(x => x.item.id === quoteItemId);
    if (!result) {
      const container = useBackup ? this.backup : this.container;
      const item =
        container.items?.find(x => x.id === quoteItemId) ??
        container.excludedItems?.find(x => x.id === quoteItemId) ??
        container.deletedItems?.find(x => x.id === quoteItemId);

      const data =
        container.itemsData?.find(x => x.id == quoteItemId) ??
        container.excludedItemsData?.find(x => x.id == quoteItemId) ??
        container.deletedItemsData?.find(x => x.id == quoteItemId);
      const buyInData =
        container.itemsBuyInData?.find(x => x.id == quoteItemId) ??
        container.excludedItemsBuyInData?.find(x => x.id == quoteItemId) ??
        container.deletedItemsBuyInData?.find(x => x.id == quoteItemId);
      const price =
        container.itemPrices?.find(x => x.id == quoteItemId) ??
        container.excludedItemPrices?.find(x => x.id == quoteItemId) ??
        container.deletedItemPrices?.find(x => x.id == quoteItemId);
      if (!item || !price) throw new Error(LangQuoteContainer.invalidQuoteItemId()); // Localized
      result = {
        item: item,
        data: data ?? null,
        buyInData: buyInData?.buyInData ?? undefined,
        price: price
      };
      cache.push(result);
    }

    return result;
  }

  quoteItemContainer(quoteItemId: string, useBackup = false): QuoteItemContainer {
    return this.quoteItemContainerCache(quoteItemId, useBackup);
  }

  quoteItemContainerClone(quoteItemId: string, useBackup = false): QuoteItemContainer {
    return this.cloneItemContainer(this.quoteItemContainerCache(quoteItemId, useBackup));
  }

  /**
   * Create a new quote item and add it to this quote instance. will ensure the quote items list is valid first before adding
   *
   * @param input parameters for the new quote item
   * @returns a valid object on success
   */
  async createQuoteItem(
    input: ParamCreateQuoteItem,
    thumbnail?: string,
    thumbnailExt?: string
  ): NullPromise<QuoteItemContainer> {
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return null;
    }

    const newId = input.id ?? newGuid();
    //ensure we have a correct listing before adding more items
    await this.needsQuoteItems();
    const inputParam: InputCreateQuoteItem = {
      quoteId: this.quoteId,
      quoteItemId: newId,
      assets: thumbnail
        ? {
            thumbnail: {
              assetPath: this.api.createQuoteItemAssetPath('thumbnail', thumbnailExt ?? '.svg'),
              data: thumbnail,
              delete: false,
              dataBase64: null,
              hasContent: false
            }
          }
        : {},

      ...input
    };
    const result = await this.api.createQuoteItem(inputParam);
    if (result) {
      await this.addQuoteItem(
        result.quoteItem,
        result.quoteItemPrice,
        result.quoteItemProviderData,
        result.quoteItemBuyInData
      );

      await this.resetQuote(
        result.quote,
        result.resultFullQuotePrice.quotePrice,
        result.quotePresentation,
        null,
        this.container.quoteProviderData
      );
      if (result.resultFullQuotePrice.itemPrices) this.updateItemPrices(result.resultFullQuotePrice.itemPrices);
      if (result.resultFullQuotePrice.itemBuyIns) this.updateItemBuyIns(result.resultFullQuotePrice.itemBuyIns);

      await this.validateBuyInPricesForItem(result.quoteItem.id);
      await this.doAfterSave();
      return this.quoteItemContainer(result.quoteItem.id);
    }
    return null;
  }

  /**
   * update a quote item on the server and its thumbnail svg image
   * after update refreshes the quote and quote price object, and all item prices
   * @param quoteItemContainer
   * @param thumbnail
   * @returns the new refreshed copy for the quoteitem
   */
  async createQuoteItemEx(
    quoteItemContainer: QuoteItemContainer,
    assets: { [key: string]: AttachedBlobAsset },
    extraItems?: InputUpdateQuoteItems
  ): Promise<QuoteItemContainer> {
    this._lastSaveSuccessful = false;

    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return quoteItemContainer;
    }
    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput(assets, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(updateItemInput, QuoteItemRowAction.Insert, extraItems);
    return this.quoteItemContainer(quoteItemContainer.item.id);
  }

  private quoteItemContainerAsUpdateItemInput(
    assets: { [key: string]: AttachedBlobAsset },
    quoteItemContainer: QuoteItemContainer
  ): InputUpdateQuoteItem {
    // NOTE:
    // We deliberately do NOT enforce the presence of a "data" asset for
    // frame items here. Absence of a provider-data asset means "no change"
    // to provider data. This allows valid scenarios where only title,
    // description, or notes are edited without touching the underlying V6
    // configuration. Server-side logic is responsible for ensuring provider
    // data is never implicitly wiped when assets are absent.

    return {
      assets,

      quoteItem: quoteItemContainer.item,
      providerDataDifference: quoteItemContainer.differenceReport
        ? (toJsonStr(quoteItemContainer.differenceReport) ?? null)
        : null,
      quoteItemBuyInData: { data: quoteItemContainer.buyInData ?? null },
      quoteItemPrice: quoteItemContainer.price
    };
  }

  public async copyQuoteItem(quoteItemContainer: QuoteItemContainer): NullPromise<QuoteItemContainer> {
    let value: QuoteItemContainer | null = null;
    try {
      if (!(await this.copyQuoteItemBefore(quoteItemContainer))) return null;

      value = await this.copyQuoteItemExecute(quoteItemContainer);
      if (!value) return null;
      if (!(await this.copyQuoteItemAfter(quoteItemContainer, value))) {
        try {
          if (value) await this.deleteQuoteItem(value);
        } catch (e) {
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          throw new DevelopmentError(`trying to delete quoteitem after failed copy. ${(e as object).toString()}`);
        }
        return null;
      }
      return value;
    } finally {
      await this.copyQuoteItemFinally(quoteItemContainer, value);
    }
  }

  async processResultItem(item: ResultQuoteItem) {
    await this.hydrateQuoteItemProviderData(item.quoteItemProviderData);
    if (!item.quoteItem) return;
    const newContainer: QuoteItemContainer = {
      item: item.quoteItem,
      data: item.quoteItemProviderData,
      price: item.quoteItemPrice!,
      buyInData: item.quoteItemBuyInData ?? undefined
    };
    this.removeContainerCache(newContainer.item.id);
    if (this.hasItemById(newContainer.item.id)) {
      this.updateItemContainer(newContainer, false);
      this.updateItemContainer(newContainer, true);
    } else {
      if (item.quoteItem && item.quoteItemPrice)
        await this.addQuoteItem(
          item.quoteItem,
          item.quoteItemPrice,
          item.quoteItemProviderData,
          item.quoteItemBuyInData
        );
    }
    await this.validateBuyInPricesForItem(item.quoteItem.id);
  }

  async internalUpdateQuoteItems(primaryItem: UpdateQuoteItem | null, extraItems: InputUpdateQuoteItems) {
    //we need to save the quote first now, because there is a chance that the Quote Calculations which are
    //returned as part of ssi, but stored on the quote block storage.
    //we only want to update quote without updating any other stuff like quote items , or prices as this
    //will all be done in the stuff we are about to update
    if (this.quoteChanged()) await this.internalSaveQuote();

    const inputItems = clone(extraItems);
    if (primaryItem) inputItems.items.push(primaryItem);
    const result = await this.api.updateQuoteItems(inputItems);
    //remove deleted items from local cache
    inputItems.items.forEach(element => {
      if (
        flagEquals(
          element.action,
          QuoteItemRowAction.Delete,
          QuoteItemRowAction.HardDelete,
          QuoteItemRowAction.Undelete
        ) &&
        element.input.quoteItem
      )
        this.internalDeleteQuoteItemReference(element.input.quoteItem.id, element.action);
    });
    return result;
  }

  async procesItemUpdateBatch(
    updateItemInput: InputUpdateQuoteItem | null,
    action: QuoteItemRowAction,
    extraItems?: InputUpdateQuoteItems
  ) {
    /*
    const updateItemInput: InputUpdateQuoteItem | null = quoteItemContainer
      ? {
          quoteItemId: quoteItemContainer.item.id,
          providerDataDifference: quoteItemContainer.differenceReport
            ? toJsonStr(quoteItemContainer.differenceReport) ?? null
            : null,
          quoteItem: quoteItemContainer.item,
          quoteItemProviderData: quoteItemContainer.data,
          quoteItemBuyInData: { data: quoteItemContainer.buyInData ?? null },
          quoteItemPrice: quoteItemContainer.price
        }
      : null;
*/
    const result = await this.internalUpdateQuoteItems(
      updateItemInput
        ? {
            action: action,
            input: updateItemInput
          }
        : null,
      extraItems ?? { items: [] }
    );
    const resultQuote = result?.quote;
    const resultFullQuotePrice = result?.resultFullQuotePrice ?? undefined;
    const resultItems = result?.items ?? [];
    const resultQuotePresentation = result?.quotePresentation ?? undefined;

    if (resultFullQuotePrice && resultQuote) {
      await this.afterItemUpdateProcessing(resultItems, resultFullQuotePrice, resultQuote, resultQuotePresentation);

      return true;
    }
    return false;
  }

  cloneItemContainer(qic: QuoteItemContainer): QuoteItemContainer {
    return {
      buyInData: clone(qic.buyInData),
      data: clone(qic.data),
      item: clone(qic.item),
      price: clone(qic.price)
    };
  }

  /**
   * update a quote item on the server and its thumbnail svg image
   * after update refreshes the quote and quote price object, and all item prices
   * @param quoteItemContainer
   * @param thumbnail
   * @returns the new refreshed copy for the quoteitem
   */
  async saveAndUpdateQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    assets: { [key: string]: AttachedBlobAsset },
    extraItems?: InputUpdateQuoteItems,
    forceSave?: boolean
  ): Promise<QuoteItemContainer> {
    this._lastSaveSuccessful = false;

    if (!forceSave && this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return quoteItemContainer;
    }

    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput(assets, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(updateItemInput, QuoteItemRowAction.Update, extraItems);

    return this.quoteItemContainer(quoteItemContainer.item.id);
  }

  public async runAndSaveSSI(_itemFilter?: string[] | undefined, _extraItems?: QuoteItemContainer[]) {
    if (!isSupplierUsingSSI(this.quote.supplierId)) return;

    const infoText = (s: string, header = LangQuoteContainer.preparingToUpdate()) => `# ${header}
                + ${s}`;

    const progress = await this.interaction.beginProgress(
      LangQuoteContainer.updatingSupplierQuoteItem(),
      3,
      infoText(LangQuoteContainer.gettingUpdatesFromSupplier())
    );
    try {
      //process ssi with new information
      //build an ssi processor that will build the quote exluding the deleted item, so that we get data
      //based on its non-existence
      const ssiProcessor = this.ssiProcessor(_itemFilter, _extraItems);
      const ssiInputs = await ssiProcessor.processSSI();
      await progress.setInformation(infoText(LangQuoteContainer.supplierProcessingComplete()));
      if (ssiInputs && ssiInputs.items.length > 0) await this.saveAndUpdateQuoteItems(ssiInputs);
    } finally {
      await progress.close();
    }
  }

  async saveAndUpdateQuoteItems(extraItems: InputUpdateQuoteItems): Promise<QuoteItemContainer[]> {
    this._lastSaveSuccessful = false;

    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return [];
    }

    this._lastSaveSuccessful = await this.procesItemUpdateBatch(null, QuoteItemRowAction.Update, extraItems);

    // Inserted rows are back with their server-assigned CommonIds, so any batch membership staged against
    // a client-minted item id (per-batch SSI charges) can now be moved into commonId space.
    this.resolvePendingItemBatches();
    await this.reshapeAfterSSI();

    const result: QuoteItemContainer[] = [];
    extraItems.items
      .filter(x => !flagEquals(x.action, QuoteItemRowAction.Delete, QuoteItemRowAction.HardDelete))
      .forEach(element => {
        if (element.input.quoteItem) result.push(this.quoteItemContainer(element.input.quoteItem?.id));
      });
    return result;
  }

  /**
   * Put the quote back into shape once an SSI run has landed (Task 230006).
   *
   * An SSI run can create a charge, remove one, or move one between containers, and by then setItemBatch has
   * already placed the affected lines in the grouping document. What was missing is everything downstream of
   * that: the flat presentation order still described the OLD shape, so the table kept rendering a per-batch
   * charge at the bottom of the quote until the next full reload - the pricing updated in place while the
   * layout silently lagged behind it.
   *
   * Also stages the document itself. Without that the shape lives only in the link rows and is re-derived by
   * the load-time heal, which works but leaves the blob - the thing the rules call the source of truth -
   * behind the truth until then, and leaves the reports reading the stale order in the meantime.
   */
  protected async reshapeAfterSSI(): Promise<void> {
    if (!this.itemGrouping || !this.container.quotePresentation) return;

    // performItemSorting rewrites itemDisplayOrder by flattening the document, so this is one call rather
    // than an order computed here and hoping the two agree.
    await this.performItemSorting();
    this.updatePendingQuoteAssets({
      [quoteItemGroupingAssetKey]: {
        assetPath: this.api.createQuoteAssetPath(quoteItemGroupingAssetKey, '.json'),
        data: serializeGroupingDocument(this.itemGrouping),
        dataBase64: null,
        delete: false,
        hasContent: false
      }
    });
  }

  async afterItemUpdateProcessing(
    resultItems: ResultQuoteItem[],
    resultFullQuotePrice: ResultFullQuotePrice,
    resultQuote: Quote,
    resultQuotePresentation?: QuotePresentation
  ) {
    for (const resultItem of resultItems) await this.processResultItem(resultItem);

    //saving item doesn't change presentation layer, pass in null
    await this.resetQuote(
      resultQuote,
      resultFullQuotePrice.quotePrice,
      resultQuotePresentation ?? null,
      null,
      this.container.quoteProviderData
    );

    if (resultFullQuotePrice?.itemPrices) this.updateItemPrices(resultFullQuotePrice.itemPrices);
    if (resultFullQuotePrice.itemBuyIns) this.updateItemBuyIns(resultFullQuotePrice.itemBuyIns);

    await this.validateBuyInPrices();
    await this.doAfterSave();
  }

  public async deleteQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    extraItems?: InputUpdateQuoteItems
  ): Promise<boolean> {
    this._lastSaveSuccessful = false;
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return false;
    }

    await this.needsQuoteItems();
    await this.doBeforeDeleteQuoteItem(quoteItemContainer);
    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput({}, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(updateItemInput, QuoteItemRowAction.Delete, extraItems);

    if (this._lastSaveSuccessful) await this.doAfterDeleteQuoteItem(quoteItemContainer);
    return this._lastSaveSuccessful;
  }
  public async undeleteQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    extraItems?: InputUpdateQuoteItems
  ): Promise<QuoteItemContainer | null> {
    this._lastSaveSuccessful = false;
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return null;
    }

    await this.needsQuoteItems();
    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput({}, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(
      updateItemInput,
      QuoteItemRowAction.Undelete,
      extraItems
    );
    const result = this.quoteItemContainer(quoteItemContainer.item.id);
    if (this._lastSaveSuccessful) await this.doAfterUndeleteQuoteItem(result);
    return result;
  }
  protected async doBeforeDeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    // do nothing
  }

  removeContainerCache(id: string) {
    let idx = this.itemContainerCache.findIndex(x => x.item.id === id);
    if (idx >= 0) this.itemContainerCache.splice(idx, 1);
    const found = idx >= 0;
    idx = this.itemContainerCacheBak.findIndex(x => x.item.id === id);
    if (idx >= 0) this.itemContainerCacheBak.splice(idx, 1);
    return found;
  }

  /**
   * Saves an already-mutated item container and shows the save indicator.
   *
   * Takes no assets. It previously took a `thumbnail` string whose only reachable
   * effect was a delete: passing '' sent `thumbnail: { delete: true }` and passing an
   * image sent no assets at all, so it could never upload one. Both callers (shipping
   * and freehand line items) passed '' literally and neither type carries a thumbnail,
   * so the delete was inert - but it was the same shape as bug 230276 and the server
   * now rejects it outright. Callers that need to write an asset should use
   * saveAndUpdateQuoteItem directly.
   */
  async saveAndUpdateQuoteItemWithIndicator(quoteItemContainer: QuoteItemContainer): Promise<QuoteItemContainer> {
    if (!this.changedItem(quoteItemContainer.item.id)) {
      this._lastSaveSuccessful = true;
      return quoteItemContainer;
    }

    let result: QuoteItemContainer = quoteItemContainer;
    this._lastSaveSuccessful = await this.interaction.saveWithIndicator(async () => {
      if (!quoteItemContainer) return false;
      try {
        const newContainer = await this.saveAndUpdateQuoteItem(quoteItemContainer, {});
        if (newContainer.item.recordVersion !== quoteItemContainer.item.recordVersion) {
          result = newContainer;
          return true;
        }
      } catch {
        return false;
      }
      return false;
    });
    return result;
  }

  async saveAndUpdateQuoteItemPrice(qic: QuoteItemContainer): Promise<QuoteItemContainer> {
    const quoteItemPrice = qic.price;
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return this.quoteItemContainer(quoteItemPrice.id);
    }

    const updateBuyInItem = (buyInData: string, useBackup: boolean) => {
      const container = useBackup ? this.backup : this.container;
      const idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id === quoteItemPrice.id) ?? -1;

      if (container.itemsBuyInData) {
        if (idxBuyInData > -1 && !buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
        else if (idxBuyInData > -1 && buyInData) container.itemsBuyInData[idxBuyInData].buyInData = buyInData;
        else if (idxBuyInData < 0 && buyInData)
          container.itemsBuyInData.push({
            buyInData: buyInData,
            dateCreated: localDateTimeToServer(new Date()),
            id: quoteItemPrice.id,
            recordVersion: ''
          });
      }
    };

    const result = await this.api.updateQuoteItem({
      quoteItem: qic.item,
      assets: {},
      providerDataDifference: null,
      quoteItemBuyInData: null,
      quoteItemPrice: quoteItemPrice
    });
    if (result?.quoteItemPrice && result.resultFullQuotePrice) {
      if (result.quoteItemBuyInData) {
        const newBuyInData = result.quoteItemBuyInData;
        updateBuyInItem(newBuyInData, false);
        updateBuyInItem(newBuyInData, true);
      }

      await this.afterItemUpdateProcessing([result], result.resultFullQuotePrice, result.quote, undefined);

      const container = this.quoteItemContainer(quoteItemPrice.id);
      this.interaction.notifySuccess(LangQuoteContainer.itemPriceUpdated({ title: container?.item.title }));
      return container;
    }

    return this.quoteItemContainer(quoteItemPrice.id);
  }

  /**
   * replaces all item price objects with a fresh set from the server.
   * @param prices a new set of item prices
   */
  updateItemPrices(prices: QuoteItemPrice[]) {
    const update = (list?: QuoteItemPrice[] | null) => {
      list?.forEach(itemPrice => {
        const newPrice = prices.find(x => x.id === itemPrice.id);
        if (newPrice) Object.assign(itemPrice, newPrice);
      });
    };
    update(this.container.itemPrices);
    update(this.backup.itemPrices);
  }

  updateItemBuyIns(buyIns: QuoteItemBuyInData[]) {
    const update = (list?: QuoteItemBuyInData[] | null) => {
      list?.forEach(itemBuyIn => {
        const newBuyIn = buyIns.find(x => x.id === itemBuyIn.id);
        if (newBuyIn) Object.assign(itemBuyIn, newBuyIn);
      });
    };
    update(this.container.itemsBuyInData);
    update(this.backup.itemsBuyInData);
  }

  quoteAddressChanged(): boolean {
    return !compare(this.backup.quote?.shippingAddress, this.container.quote?.shippingAddress);
  }
  private hasPendingQuoteAssets(): boolean {
    if (!this.quoteUpdateAssets) return false;
    return Object.keys(this.quoteUpdateAssets).some(_key => true);
  }

  private stripServiceProviderData(quote: Quote | null): Quote | null {
    if (!quote) return quote;
    return { ...quote, serviceProviderData: null };
  }
  updatePendingQuoteAssets(assets: { [key: string]: AttachedBlobAsset | null | undefined }): void {
    if (!assets) return;
    this.quoteUpdateAssets = this.quoteUpdateAssets ?? {};
    Object.entries(assets).forEach(([key, asset]) => {
      if (!asset) delete this.quoteUpdateAssets![key];
      else this.quoteUpdateAssets![key] = asset;
    });
    if (!this.hasPendingQuoteAssets()) this.quoteUpdateAssets = null;
  }
  quoteChanged(): boolean {
    return (
      !compare(this.stripServiceProviderData(this.backup.quote), this.stripServiceProviderData(this.container.quote)) ||
      !compare(this.backup.quoteProviderData, this.container.quoteProviderData) ||
      !compare(this.backup.quotePrice, this.container.quotePrice) ||
      !compare(this.backup.quotePresentation, this.container.quotePresentation) ||
      this.hasPendingQuoteAssets()
    );
  }

  changed(): boolean {
    return !compare(this.backup, this.container) || this.hasPendingQuoteAssets();
  }

  public async makeCopy(_clearShippingAddress = false): NullPromise<QuoteContainer> {
    return null;
  }

  public async makeAlternative(_forSupplierReview = false): NullPromise<QuoteContainer> {
    return null;
  }

  public async deleteQuote(): Promise<boolean> {
    return false;
  }

  public async getBuyInCosts(_buyInItems?: QuoteFrameBuyInItem[]): Promise<StockLookupViewExtra[] | undefined> {
    return undefined;
  }

  public lookupBuyInData(
    quoteItemContainer: QuoteItemContainer,
    code: string,
    supplierCode: string,
    lookup: string
  ): StockLookupViewExtra | undefined {
    if (!quoteItemContainer.buyInData) return;

    const buyIns = fromJsonStr<StockLookupViewExtra[]>(quoteItemContainer.buyInData);

    if (!buyIns) return;

    // eslint-disable-next-line consistent-return
    return buyIns.find(x => x.code == code && x.supplierCode == supplierCode && x.lookup == lookup);
  }

  public itemIsNotSortable(item: QuoteItem) {
    return isShipping(item) || isSSI(item) || isDealerQuotePriceAdjustment(item);
  }

  protected async loadSupplierName() {
    if (isEmptyOrSpace(this._supplierName))
      this._supplierName = await getQuoteSupplierDisplayName(this.quote.supplierId);
  }

  /**
   * Look up an active quote item by id for the pricing-total aggregates. Returns the item only
   * when it is active; excluded-from-pricing and deleted items return undefined so callers skip
   * them (excluded items must not contribute to pricing totals). Throws only for a truly orphaned
   * id (price with no matching item anywhere), which is a development error.
   */
  protected getItemById(id: string): QuoteItem | undefined {
    const item = this.container?.items?.find(x => x.id === id);
    if (item) return item;
    if (this.container.excludedItems?.find(x => x.id === id) || this.container.deletedItems?.find(x => x.id === id))
      return undefined;
    throw new DevelopmentError(`${id} missing in list`);
  }

  protected quoteStateBeforeChange(_state: QuoteState, _original: QuoteState): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected quoteStateAfterChange(_state: QuoteState, _original: QuoteState): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected copyQuoteItemBefore(_quoteItemContainer: QuoteItemContainer): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected copyQuoteItemAfter(
    _quoteItemContainer: QuoteItemContainer,
    _newQuoteItemContainer: QuoteItemContainer
  ): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected async copyQuoteItemFinally(
    _quoteItemContainer: QuoteItemContainer,
    _newQuoteItemContainer: QuoteItemContainer | null
  ): Promise<void> {
    //
  }

  protected async copyQuoteItemExecute(quoteItemContainer: QuoteItemContainer): NullPromise<QuoteItemContainer> {
    const result = await this.api.duplicateQuoteItem({
      quoteItemId: quoteItemContainer.item.id
    });
    if (result) {
      await this.addQuoteItem(
        result.quoteItem,
        result.quoteItemPrice,
        result.quoteItemProviderData,
        result.quoteItemBuyInData?.buyInData ?? null
      );
      await this.resetQuote(
        result.quote,
        result.resultFullQuotePrice.quotePrice,
        result.quotePresentation,
        null,
        this.container.quoteProviderData
      );
      await this.validateBuyInPrices();
      await this.doAfterSave();
      return this.quoteItemContainer(result.quoteItem.id);
    }
    return null;
  }

  protected async doAfterDeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    //DoNothing
  }
  protected async doAfterUndeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    //DoNothing
  }

  protected internalIsReadonly(): boolean {
    return false;
  }

  /**
   * replaces the backups and originals of the quote objects with this new set of objects to become the master
   * @param quote
   * @param quotePrice
   * @param quotePresentation
   */
  protected async resetQuote(
    quote: Quote,
    quotePrice: QuotePrice,
    quotePresentation: QuotePresentation | null,
    siblings: QuoteSetSibling[] | null,
    providerData: QuoteProviderData | null
  ) {
    if (this.container.quote) Object.assign(this.container.quote, quote);
    else this.container.quote = quote;

    this.container.quote.serviceProviderData = null; //we never want to keep service provider data in memory
    const tmpProviderData =
      providerData ?? (await createQuoteProviderData(quote.serviceProvider, quote.supplierId, quote.quoteOwnerId));
    this.container.quoteProviderData = await validateAndUpgradeQuoteProviderData(tmpProviderData);
    if (quotePresentation) this.container.quotePresentation = quotePresentation;
    else if (!this.container.quotePresentation) throw new DevelopmentError('Missing QuotePresentation'); //should only occur on updates

    if (this.container.quotePrice) Object.assign(this.container.quotePrice, quotePrice);
    else this.container.quotePrice = quotePrice;

    if (siblings) this.container.siblings = siblings;

    this.backup.quote = this.clone(quote);
    this.backup.quoteProviderData = this.clone(this.container.quoteProviderData);
    this.backup.quotePrice = this.clone(quotePrice);

    //we dont pass in siblings if we didn't update the quote directly
    if (siblings) this.backup.siblings = this.clone(siblings);

    if (quotePresentation) this.backup.quotePresentation = this.clone(quotePresentation);
  }

  /**
   * execute all bound events after any save operation to allow for re-rendering and refreshing of state
   */
  protected async doAfterSave(): Promise<void> {
    await runEventNotify(this.afterSave);
  }

  protected async validateBuyInPricesForItem(id: string) {
    if (this.isBuyInDataLocked) return;
    const buyInData = this.container.itemsBuyInData?.find(x => x.id == id);

    const validationIssueIndex = this.priceValidation?.findIndex(x => x.id == id) ?? -1;

    if (validationIssueIndex > -1) this.priceValidation?.splice(validationIssueIndex, 1);

    if (buyInData) {
      await this.reportBuyInValidation(buyInData);
    }
  }

  protected async validateBuyInPrices() {
    if (this.isBuyInDataLocked) return;
    this.priceValidation = null;
    this.container.itemsBuyInData?.map(async x => {
      await this.reportBuyInValidation(x);
    });
  }

  protected async addQuoteItem(
    quoteItem: QuoteItem,
    quoteItemPrice: QuoteItemPrice,
    quoteItemProviderData: QuoteItemProviderData | null,
    quoteItemBuyInData: string | null
  ) {
    await this.hydrateQuoteItemProviderData(quoteItemProviderData);
    if (
      this.container.items?.find(x => x.id === quoteItem.id) ??
      this.container.deletedItems?.find(x => x.id === quoteItem.id)
    ) {
      await showDevelopmentError(`Quote item ${quoteItem.id} already exists in the quote`);
      return;
    }

    const isDeleted = quoteItem.state === QuoteItemState.Deleted;
    if (isDeleted) {
      this.container.deletedItems ??= [];
      this.container.deletedItems.push(quoteItem);
    } else {
      this.container.items ??= [];
      this.container.items.push(quoteItem);
    }
    if (quoteItemProviderData) {
      if (isDeleted) {
        this.container.deletedItemsData ??= [];
        this.container.deletedItemsData.push(quoteItemProviderData);
      } else {
        this.container.itemsData ??= [];
        this.container.itemsData.push(quoteItemProviderData);
      }
    }
    if (quoteItemBuyInData) {
      const buyInEntry: QuoteItemBuyInData = {
        id: quoteItem.id,
        buyInData: quoteItemBuyInData,
        dateCreated: localDateTimeToServer(new Date()),
        recordVersion: ''
      };
      if (isDeleted) {
        this.container.deletedItemsBuyInData ??= [];
        this.container.deletedItemsBuyInData.push(buyInEntry);
      } else {
        this.container.itemsBuyInData ??= [];
        this.container.itemsBuyInData.push(buyInEntry);
      }
    }
    if (this.container.itemPrices?.find(x => x.id === quoteItemPrice.id)) {
      await showDevelopmentError(`Quote item Price ${quoteItemPrice.id} already exists in the quote`);
      return;
    }

    if (this.container.itemPrices && this.container.deletedItemPrices) {
      if (isDeleted) this.container.deletedItemPrices.push(quoteItemPrice);
      else this.container.itemPrices.push(quoteItemPrice);
    } else {
      // Initialize if needed
      if (isDeleted) {
        this.container.deletedItemPrices ??= [];
        this.container.deletedItemPrices.push(quoteItemPrice);
      } else {
        this.container.itemPrices ??= [];
        this.container.itemPrices.push(quoteItemPrice);
      }
    }

    if (isDeleted) {
      this.backup.deletedItems ??= [];
      this.backup.deletedItems.push(this.clone(quoteItem));
    } else {
      this.backup.items ??= [];
      this.backup.items.push(this.clone(quoteItem));
    }
    if (quoteItemProviderData) {
      if (isDeleted) {
        this.backup.deletedItemsData ??= [];
        this.backup.deletedItemsData.push(this.clone(quoteItemProviderData));
      } else {
        this.backup.itemsData ??= [];
        this.backup.itemsData.push(this.clone(quoteItemProviderData));
      }
    }
    if (quoteItemBuyInData) {
      const buyInBackup: QuoteItemBuyInData = {
        id: quoteItem.id,
        buyInData: quoteItemBuyInData,
        dateCreated: localDateTimeToServer(new Date()),
        recordVersion: ''
      };
      if (isDeleted) {
        this.backup.deletedItemsBuyInData ??= [];
        this.backup.deletedItemsBuyInData.push(buyInBackup);
      } else {
        this.backup.itemsBuyInData ??= [];
        this.backup.itemsBuyInData.push(buyInBackup);
      }
    }
    if (isDeleted) {
      this.backup.deletedItemPrices ??= [];
      this.backup.deletedItemPrices.push(this.clone(quoteItemPrice));
    } else {
      this.backup.itemPrices ??= [];
      this.backup.itemPrices.push(this.clone(quoteItemPrice));
    }

    // Resolve this row's staged batch membership BEFORE the sort below, not after the whole batch of rows has
    // been processed. The membership could only be staged against the client-minted item id (the server mints
    // the CommonId the link table keys on), so it becomes resolvable exactly here - the moment the inserted
    // row is in hand. Resolving it later meant this sort ran with the charge still unplaced: it landed at the
    // bottom of the quote, painted there, and then jumped up into its batch when the placement caught up.
    this.resolvePendingItemBatch(quoteItem);

    this.applySortedLiveItems(await this.internalSortItemsAsList());
    this.backup.items = clone(this.container.items);
    this.backup.excludedItems = clone(this.container.excludedItems);
  }

  /**
   * perform an insert of text based blob data such as svg on the server
   * @param oldItemPath an old name to delete when uploading
   * @param newItemPath a new name path to use for this insert
   * @param imageData text based data such as svg
   */
  protected async postImageBlob(oldItemPath: string, newItemPath: string, imageData: string | null) {
    if (!imageData) return;
    const base64Index = imageData.indexOf('base64,');
    const data = base64Index >= 0 ? imageData.substring(imageData.indexOf(',') + 1) : btoa(imageData);

    await this.blobApi.updateFileByVirtualPath({
      oldVirtualPath: oldItemPath,
      newVirtualPath: newItemPath,
      data: data
    });
  }

  /**
   * The canonical display order.
   *
   * With the grouping document loaded, flattening it IS the answer - every item in its final position,
   * charges included, because the document's own layout rules already placed them (see
   * normalizeGroupingDocument). This function used to reconstruct that placement for itself: bucket the
   * forced items by batch from the link map, then flush each bucket as the walk passed that batch's last
   * member. It was one of three independent reconstructions of one rule, and the only one that read the link
   * map for charges while reading the document for everything else - so mid-regroup it could disagree with
   * both of the others.
   *
   * Before the document loads, the legacy computation stands in: sortable items in presentation order, then
   * the forced items ranked SSI → shipping → price adjustment at the end. That is the pre-grouping behaviour,
   * and by the layout rules it is exactly what the document reduces to when it holds no groups.
   */
  private getAdjustedPresentationOrder(items: QuoteItem[]): string[] {
    if (this.itemGrouping) {
      const live = new Set(items.map(x => x.commonId));
      const placed = flattenGroupingToDisplayOrder(this.itemGrouping).filter(id => live.has(id));
      const seen = new Set(placed);
      // Items the document has not caught up with (added since the last reconcile) and the deleted items,
      // which the document never holds. Both keep their place at the end rather than dropping out of the
      // order - which is where the table renders deleted rows anyway.
      return [...placed, ...items.filter(x => !seen.has(x.commonId)).map(x => x.commonId)];
    }

    const sortableOrder: string[] = [];
    const forcedItems: QuoteItem[] = items.filter(item => this.itemIsNotSortable(item));
    const order = this.container.quotePresentation?.itemDisplayOrder ?? [];

    order.forEach(commonId => {
      const item = items.find(x => x.commonId === commonId);
      if (item && !this.itemIsNotSortable(item)) sortableOrder.push(commonId);
    });
    items
      .filter(x => !this.itemIsNotSortable(x) && !sortableOrder.includes(x.commonId))
      .forEach(item => {
        sortableOrder.push(item.commonId);
      });

    // Fixed relative order within the forced block, unchanged: SSI, then shipping, then price adjustment.
    const forcedRank = (item: QuoteItem): number => {
      if (isShipping(item)) return 1;
      if (isSSI(item)) return 0;
      return 2;
    };

    return [...sortableOrder, ...[...forcedItems].sort((a, b) => forcedRank(a) - forcedRank(b)).map(x => x.commonId)];
  }

  private async internalSortItemsAsList(items?: QuoteItem[]) {
    // default to the live union so excluded items keep their place in itemDisplayOrder; callers
    // that pass an explicit list (e.g. the full load, incl. deleted) drive their own partitioning.
    const sortingItems = items ?? this.liveItems();
    if (!this.container.quotePresentation) {
      await showDevelopmentError(LangQuoteContainer.quotePresentationMissing()); // Localized
      return sortingItems;
    }
    const sortedItems: QuoteItem[] = [];
    const displayOrder = this.getAdjustedPresentationOrder(sortingItems);
    this.container.quotePresentation.itemDisplayOrder = displayOrder;

    //create a list ordered by the quote presentation
    displayOrder.forEach(commonId => {
      const item = sortingItems.find(item => item.commonId === commonId);
      if (item) {
        sortedItems.push(item);
      }
    });
    if (sortedItems.length !== sortingItems.length) {
      sortedItems.push(...sortingItems.filter(item => !displayOrder.includes(item.commonId)));
    }
    return sortedItems;
  }

  private updateItemContainer(updatedItemContainer: QuoteItemContainer, useBackup: boolean) {
    const container = useBackup ? this.backup : this.container;
    const id = updatedItemContainer.item.id;
    // an item may live in the active, excluded or deleted partition; update it wherever it is so a
    // state flip (e.g. excluded -> active) refreshes the right list. Re-partitioning by state is a
    // separate step (repartitionByState).
    const itemLists = [container.items, container.excludedItems, container.deletedItems];
    const dataLists = [container.itemsData, container.excludedItemsData, container.deletedItemsData];
    const priceLists = [container.itemPrices, container.excludedItemPrices, container.deletedItemPrices];
    const buyInLists = [container.itemsBuyInData, container.excludedItemsBuyInData, container.deletedItemsBuyInData];

    for (const list of itemLists) {
      const idx = list?.findIndex(x => x.id === id) ?? -1;
      if (idx >= 0 && list) {
        list[idx] = useBackup ? this.clone(updatedItemContainer.item) : updatedItemContainer.item;
        break;
      }
    }
    if (updatedItemContainer.data) {
      for (const list of dataLists) {
        const idx = list?.findIndex(x => x.id === updatedItemContainer.data?.id) ?? -1;
        if (idx >= 0 && list) {
          list[idx] = useBackup ? this.clone(updatedItemContainer.data) : updatedItemContainer.data;
          break;
        }
      }
    }
    for (const list of priceLists) {
      const idx = list?.findIndex(x => x.id === updatedItemContainer.price.id) ?? -1;
      if (idx >= 0 && list) {
        list[idx] = useBackup ? this.clone(updatedItemContainer.price) : updatedItemContainer.price;
        break;
      }
    }

    // buy-in lives in the partition holding the item; locate that partition's buy-in list.
    const buyInList =
      buyInLists.find(list => list?.some(x => x.id === id)) ??
      (container.items?.some(x => x.id === id)
        ? (container.itemsBuyInData ??= [])
        : container.excludedItems?.some(x => x.id === id)
          ? (container.excludedItemsBuyInData ??= [])
          : (container.itemsBuyInData ??= []));
    const idxBuyInData = updatedItemContainer.buyInData ? (buyInList?.findIndex(x => x.id === id) ?? -1) : -1;
    if (buyInList) {
      if (idxBuyInData > -1 && !updatedItemContainer.buyInData) buyInList.splice(idxBuyInData, 1);
      else if (idxBuyInData > -1 && updatedItemContainer.buyInData)
        buyInList[idxBuyInData].buyInData = updatedItemContainer.buyInData;
      else if (idxBuyInData < 0 && updatedItemContainer.buyInData)
        buyInList.push({
          buyInData: updatedItemContainer.buyInData,
          dateCreated: localDateTimeToServer(new Date()),
          id: id,
          recordVersion: ''
        });
    }
  }

  private async reportBuyInValidation(itemBuyInData: QuoteItemBuyInData) {
    if (this.isBuyInDataLocked) return;

    const metaDataForItem = fromJsonStr<StockLookupViewExtra[]>(itemBuyInData.buyInData);

    if (metaDataForItem) {
      const buyins = await this.getBuyInCosts(
        metaDataForItem.map(y => {
          return {
            code: y.code,
            partCode: '',
            extraDetails: {
              LibCode: y.libCode,
              SuppCode: y.supplierCode,
              SupplierId: y.supplierId
            },
            quantity: 1,
            resourceType: 0,
            decription: ''
          };
        })
      );

      //Once V6 returns the libcode, we need to filter on that as well
      // eslint-disable-next-line array-callback-return
      metaDataForItem.map(y => {
        const serverVersion = buyins?.find(
          z => z.code == y.code && y.supplierCode == z.supplierCode && y.supplierId == z.supplierId
        );

        // Check for missing costs
        if (y.cost == null || !serverVersion) {
          this.addPriceValidation(itemBuyInData.id, y.code, 'Missing cost price');
        }

        //Check for catalogue updates updates
        if (y.version !== serverVersion?.version) {
          this.addPriceValidation(itemBuyInData.id, y.code, 'Buy-in catalogue has changed');
        }
      });
    }
  }

  private internalDeleteQuoteItemReference(quoteItemId: string, action: QuoteItemRowAction) {
    function remove(list: { id: string }[]) {
      const idx = list.findIndex(x => x.id === quoteItemId) ?? -1;
      if (idx >= 0) list.splice(idx, 1);
    }

    if (action === QuoteItemRowAction.HardDelete) {
      if (this.container.items) remove(this.container.items);
      if (this.container.itemPrices) remove(this.container.itemPrices);
      if (this.container.itemsData) remove(this.container.itemsData);
      if (this.container.itemsBuyInData) remove(this.container.itemsBuyInData);
      // Also remove from deleted partitions if already soft-deleted earlier
      if (this.container.deletedItems) remove(this.container.deletedItems);
      if (this.container.deletedItemPrices) remove(this.container.deletedItemPrices);
      if (this.container.deletedItemsData) remove(this.container.deletedItemsData);
      if (this.container.deletedItemsBuyInData) remove(this.container.deletedItemsBuyInData);

      if (this.backup.items) remove(this.backup.items);
      if (this.backup.itemPrices) remove(this.backup.itemPrices);
      if (this.backup.itemsData) remove(this.backup.itemsData);
      if (this.backup.itemsBuyInData) remove(this.backup.itemsBuyInData);
      if (this.backup.deletedItems) remove(this.backup.deletedItems);
      if (this.backup.deletedItemPrices) remove(this.backup.deletedItemPrices);
      if (this.backup.deletedItemsData) remove(this.backup.deletedItemsData);
      if (this.backup.deletedItemsBuyInData) remove(this.backup.deletedItemsBuyInData);
    } else if (action === QuoteItemRowAction.Delete) {
      const item = this.container.items?.find(x => x.id === quoteItemId);
      if (item) {
        // Remove from items
        this.container.items = this.container.items?.filter(x => x.id !== quoteItemId) ?? null;
        // Add to deletedItems
        if (!this.container.deletedItems) this.container.deletedItems = [];
        this.container.deletedItems.push(item);

        // Move provider data
        const data = this.container.itemsData?.find(x => x.id === quoteItemId);
        if (data) {
          this.container.itemsData = this.container.itemsData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.container.deletedItemsData) this.container.deletedItemsData = [];
          this.container.deletedItemsData.push(data);
        }
        // Move buy-in data
        const buyIn = this.container.itemsBuyInData?.find(x => x.id === quoteItemId);
        if (buyIn) {
          this.container.itemsBuyInData = this.container.itemsBuyInData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.container.deletedItemsBuyInData) this.container.deletedItemsBuyInData = [];
          this.container.deletedItemsBuyInData.push(buyIn);
        }
        // Move price
        const price = this.container.itemPrices?.find(x => x.id === quoteItemId);
        if (price) {
          this.container.itemPrices = this.container.itemPrices?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.container.deletedItemPrices) this.container.deletedItemPrices = [];
          this.container.deletedItemPrices.push(price);
        }

        const backupItem = this.backup.items?.find(x => x.id === quoteItemId);
        if (backupItem) {
          // Remove from backup items
          this.backup.items = this.backup.items?.filter(x => x.id !== quoteItemId) ?? null;
          // Add to backup deletedItems
          if (!this.backup.deletedItems) this.backup.deletedItems = [];
          this.backup.deletedItems.push(backupItem);
        }
        // Backup provider data
        const backupData = this.backup.itemsData?.find(x => x.id === quoteItemId);
        if (backupData) {
          this.backup.itemsData = this.backup.itemsData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.backup.deletedItemsData) this.backup.deletedItemsData = [];
          this.backup.deletedItemsData.push(backupData);
        }
        // Backup buy-in data
        const backupBuyIn = this.backup.itemsBuyInData?.find(x => x.id === quoteItemId);
        if (backupBuyIn) {
          this.backup.itemsBuyInData = this.backup.itemsBuyInData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.backup.deletedItemsBuyInData) this.backup.deletedItemsBuyInData = [];
          this.backup.deletedItemsBuyInData.push(backupBuyIn);
        }
        // Backup price
        const backupPrice = this.backup.itemPrices?.find(x => x.id === quoteItemId);
        if (backupPrice) {
          this.backup.itemPrices = this.backup.itemPrices?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.backup.deletedItemPrices) this.backup.deletedItemPrices = [];
          this.backup.deletedItemPrices.push(backupPrice);
        }
      }
    } else if (action === QuoteItemRowAction.Undelete) {
      // Use existing prepareForUnDelete logic which migrates item + provider/buy-in/price data
      this.prepareForUnDelete(quoteItemId);
    }

    this.removeContainerCache(quoteItemId);
  }

  /**
   * find and replace the data elements of a quoteItem with a new refreshed set of data
   * uses a clone of the data passed in
   * @param updatedItemContainer
   * @param useBackup
   */
  private replaceQuoteItem(updatedItemContainer: QuoteItemContainer, useBackup: boolean) {
    const container = useBackup ? this.backup : this.container;
    const idxItem = container.items?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    let idxData = -1;
    if (updatedItemContainer.data)
      idxData = container.itemsData?.findIndex(x => x.id === updatedItemContainer.data?.id) ?? -1;

    const idxPrice = container.itemPrices?.findIndex(x => x.id == updatedItemContainer.price.id) ?? -1;
    if (idxItem >= 0 && container.items) container.items[idxItem] = this.clone(updatedItemContainer.item);
    if (updatedItemContainer.data && idxData >= 0 && container.itemsData)
      container.itemsData[idxData] = this.clone(updatedItemContainer.data);

    let idxBuyInData = -1;
    if (updatedItemContainer.buyInData)
      idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    if (container.itemsBuyInData) {
      if (idxBuyInData > -1 && !updatedItemContainer.buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
      else if (idxBuyInData > -1 && updatedItemContainer.buyInData)
        container.itemsBuyInData[idxBuyInData].buyInData = updatedItemContainer.buyInData;
      else if (idxBuyInData < 0 && updatedItemContainer.buyInData)
        container.itemsBuyInData.push({
          buyInData: updatedItemContainer.buyInData,
          dateCreated: localDateTimeToServer(new Date()),
          id: updatedItemContainer.item.id,
          recordVersion: ''
        });
    }

    if (idxPrice >= 0 && container.itemPrices) container.itemPrices[idxPrice] = this.clone(updatedItemContainer.price);
  }

  private hasItemById(id: string) {
    return (
      (this.container?.items?.some(x => x.id === id) ||
        this.container?.excludedItems?.some(x => x.id === id) ||
        this.container?.deletedItems?.some(x => x.id === id)) ??
      false
    );
  }

  /**
   * Move each live item into the partition that matches its current `state` field. Called after a
   * grouping-driven state change (Active <-> ExcludeFromPricing) so the active/excluded lists line
   * up with the persisted states. Deleted items are left untouched.
   */
  private repartitionByState() {
    for (const item of [...(this.container.items ?? [])]) {
      if (isExcludedFromPricingQuoteItemState(item)) this.moveItemPartition(item.id, 'active', 'excluded');
    }
    for (const item of [...(this.container.excludedItems ?? [])]) {
      if (!isExcludedFromPricingQuoteItemState(item)) this.moveItemPartition(item.id, 'excluded', 'active');
    }
  }
}
export function isDeletedQuoteItemState(item: QuoteItem) {
  return item.state === QuoteItemState.Deleted;
}
export function isExcludedFromPricingQuoteItemState(item: QuoteItem) {
  return item.state === QuoteItemState.ExcludeFromPricing;
}
